/**
 * Excel host operation dispatcher.
 *
 * Thin per-operation layer that decides, at call time, whether to dispatch
 * through the FactSet intent bridge or to fall back to Office.js. Centralising
 * the gate here keeps `excelService.ts` dumb: call sites hand over intent —
 * the dispatcher figures out how to perform it.
 *
 * Scaling pattern: each new Excel intent gets one exported dispatcher
 * function here (plus its intent wrapper in `services/factset/spreadsheetIntents.ts`).
 * Call sites in `excelService.ts` only ever import the dispatcher.
 *
 * Current coverage (70 dispatchers total):
 *
 *   `factset.Spreadsheet.*` catalog — 59 dispatchers fanning out across
 *   the catalog intents. Convenience entry points like `setCellContents`
 *   / `fillRangeContents` (both → `InsertContentsToRange`) and
 *   `autofitRange` / `resizeAbsolute` (both → `ResizeExcelElements`)
 *   give call sites in `excelService.ts` a typed surface. Per-dispatcher
 *   JSDoc lives next to each function below; the wrapper-level wire
 *   shapes live in `spreadsheetIntents.ts`.
 *
 *    - `setCellContents`, `fillRangeContents`         → InsertContentsToRange
 *                                                       (`ExcelHostOpOptions.workbookName`
 *                                                        pins the write via an Excel
 *                                                        external-ref `destinationCell`;
 *                                                        see `destinationCellOf`)
 *    - `getActiveCellAddress`                         → GetActiveCellAddress
 *    - `getActiveExcelContext`, `getActiveSheetName`  → GetActiveExcelContext
 *    - `autofitRange`  (mode FIT)                     → ResizeExcelElements
 *    - `resizeAbsolute` (mode ABSOLUTE)               → ResizeExcelElements
 *    - `setCalculationMode`                           → SetCalculationMode
 *    - `getRangeInfo`                                 → GetRangeInfo
 *    - `getRangeAsCsv`                                → GetRangeAsCsv
 *    - `getWorkbookSheets`                            → GetWorkbookSheets
 *    - `getSelectedAddressRange`                      → GetSelectedAddressRange
 *    - `refreshWorksheets`                            → HarmonizedRefreshWorksheets
 *    - `applicationCalculate`                         → ApplicationCalculate
 *    - `getCalculationMode`                           → GetCalculationMode
 *    - `parseFormulas`                                → ParseFormulas
 *    - `getCharts`                                    → GetCharts
 *    - `getTables`                                    → GetTables
 *    - `deleteRange`                                  → DeleteRange
 *    - `insertRange`                                  → InsertRange
 *    - `clearRange`                                   → ClearRange
 *    - `sortRange`                                    → SortRange
 *    - `applyFormatting`                              → ApplyFormatting
 *    - `moveRange`                                    → MoveRange
 *    - `addWorksheet`                                 → AddWorksheet
 *    - `activateWorksheet`                            → ActivateWorksheet
 *    - `deleteWorksheet`                              → DeleteWorksheet
 *    - `renameWorksheet`                              → RenameWorksheet
 *    - `moveSheet`                                    → MoveSheet
 *    - `addWorkbook`                                  → AddWorkbook
 *    - `copyWorksheet`                                → CopyWorksheet
 *    - `insertWorksheetsFromBase64`                   → InsertWorksheetsFromBase64
 *    - `setWorksheetVisibility`                       → SetWorksheetVisibility
 *    - `addActiveGraph`                               → AddActiveGraph
 *    - `addExcelChart`                                → Charting.AddExcelChart
 *    - `updateExcelChart`                             → Charting.UpdateExcelChart
 *    - `addSeriesToChart`                             → Charting.AddSeriesToChart
 *    - `updateSeriesInChart`                          → Charting.UpdateSeriesInChart
 *    - `removeSeriesFromChart`                        → Charting.RemoveSeriesFromChart
 *    - `getChartImageAsBase64`                        → Charting.GetChartImageAsBase64
 *    - `addImageFromBase64`                           → AddImageFromBase64
 *    - `hideColumns`, `hideRows`                      → HideColumns / HideRows
 *    - `groupColumns`, `groupRows`,
 *      `ungroupColumns`, `ungroupRows`                → Group{Columns,Rows} / Ungroup{Columns,Rows}
 *    - `deleteShape`, `modifyShapes`, `selectShapes`  → DeleteShape / ModifyShapes / SelectShapes
 *    - `freezeUnfreezePanes`                          → FreezeUnfreezePanes
 *    - `selectRange`                                  → SelectRange
 *    - `getOpenWorkbooks`                             → GetOpenWorkbooks
 *    - `getWorkbookConnections`                       → GetWorkbookConnections
 *    - `getWorkbookNamedObjects`                      → GetWorkbookNamedObjects
 *    - `addNamedObject`, `removeNamedObject`          → AddNamedObject / RemoveNamedObject
 *    - `searchWorkbook`                               → Search
 *    - `getComment`, `setComment`,
 *      `deleteComment`, `getAllComments`              → Get/Set/Delete/GetAllComments
 *    - `getNote`, `setNote`,
 *      `deleteNote`, `getAllNotes`                    → Get/Set/Delete/GetAllNotes
 *    - `getRangeSnapshot`                             → GetRangeSnapshot
 *    - `getWorksheetsAsBase64`                        → GetWorksheetsAsBase64
 *    - `beginUndo`                                    → BeginUndo
 *    - `endUndo`                                      → EndUndo
 *
 *   `factset.Spreadsheet.InsertTtfApiResponse` — wired in Phase 4. The
 *   primary producer is the per-cell TTF dispatcher in
 *   `excelService.ts::applyWorkbookModificationsViaIntents`, which
 *   builds an `apiResponse` in-process via
 *   `services/factset/ttfCellDispatch.ts` and dispatches through the
 *   same wrapper. The `WorkbookOperation.action = "insertTtfApiResponse"`
 *   slot also routes here for the (rare) case where a producer hands
 *   a pre-built `apiResponse` envelope through that channel directly.
 *
 *    - `insertTtfApiResponse`                         → InsertTtfApiResponse
 *
 *   `factset.*` and `factset.Charting.*` sibling-namespaces
 *   (OI Remote Iframe) — 13 dispatchers (+ one poll helper) that live
 *   OUTSIDE the `factset.Spreadsheet.*` catalog. Per-dispatcher JSDoc
 *   covers wire shape, response envelope, UI consumer, and any
 *   product-surface caveats. `factset.Charting.*` is the host-agnostic
 *   ActiveGraph namespace (shared with PowerPoint) — operates on the
 *   currently-selected ActiveGraph OLE object.
 *
 *    - `previewChart`                                 → factset.PreviewChart
 *    - `downloadExcelChart`                           → factset.DownloadExcelChart
 *    - `downloadActiveGraph`                          → factset.DownloadActiveGraph
 *    - `getActiveGraphXml`                            → factset.Charting.GetActiveGraphXml  [NOT exposed to the planner; kept for dev-tools / non-agent flows only]
 *    - `setActiveGraphXml`                            → factset.Charting.SetActiveGraphXml  [NOT exposed to the planner; kept for dev-tools / non-agent flows only]
 *    - `getStylesets`                                 → factset.GetStylesets
 *    - `getTemplatesList`                             → factset.GetTemplatesList
 *    - `getUserAvailableTemplates`                    → factset.GetUserAvailableTemplates
 *    - `downloadTemplate`                             → factset.DownloadTemplate
 *    - `refreshCalculate`                             → factset.RefreshCalculate
 *    - `getRefreshStatus`                             → factset.GetRefreshStatus
 *    - `getRefresh`                                   → factset.GetRefresh
 *    - `awaitRefreshCalculate` (poll loop)            → factset.GetRefreshStatus
 *    - `getPaneStatus`                                → factset.GetPaneStatus
 *
 *   The wrapper / dispatcher / consumer summary table is in
 *   `missing-intents-v0.md` §4.4; per-intent capture is in
 *   `excel-docs/intent-details.md`.
 *
 * Pre-announced FactSet roadmap (not yet on the wire — new dispatchers go here):
 *  - `factset.Spreadsheet.ExportWorkbook`              (closes missing-intents §6.4 —
 *                                                       `getWorksheetsAsBase64`, cataloged
 *                                                       2026-05-12 as ⚗️ Experimental,
 *                                                       is the interim surface)
 *  - Shape **create**-side intents beyond
 *    `AddActiveGraph` / `AddImageFromBase64`            (`#open-shape-create` in §6 —
 *                                                       delete / modify / select are now wired)
 * See `f2_docs/excel/finster-docs/missing-intents-v0.md` §0 banner.
 *
 * Pre-announced 0-based index migration (not yet on the wire):
 *  - `getActiveExcelContext`, `getWorkbookSheets`, `addWorksheet`,
 *    `getTables`, `getCharts` will move from 1-based to 0-based indices on
 *    the wire. The dispatchers below are pass-throughs; the doc-only flip
 *    happens in the wrappers (`spreadsheetIntents.ts`) and in the bridge
 *    consumers (`excelService.ts::extractWorkbookContentViaBridge` etc.).
 *  - Tracked in `missing-intents-v0.md` → `#open-zero-index-migration`.
 *
 * When the bridge is unavailable or disabled, these functions throw so the
 * caller can fall back to its existing `Excel.run` code path. Use
 * `shouldUseExcelIntentBridge()` to decide whether to call them at all.
 */

import { isOfficeJsBypassed } from "../shared/office/officeJsBypass";
import { isFactSetSpreadsheetHost } from "../shared/factset/hostContextService";
import { isFactSetIntentBridgeAvailable } from "../shared/factset/factsetIntentTransport";
import { diag } from "./aiDiag";
import { parseCellRangeAddress } from "./excelCellAddress";
import {
  insertContentsToRangeViaIntents,
  getActiveCellAddressViaIntents,
  getActiveExcelContextViaIntents,
  resizeExcelElementsViaIntents,
  setCalculationModeViaIntents,
  getRangeInfoViaIntents,
  getRangeAsCsvViaIntents,
  getWorkbookSheetsViaIntents,
  getSelectedAddressRangeViaIntents,
  harmonizedRefreshWorksheetsViaIntents,
  applicationCalculateViaIntents,
  getCalculationModeViaIntents,
  parseFormulasViaIntents,
  getChartsViaIntents,
  getTablesViaIntents,
  getCircularReferencesViaIntents,
  getCellErrorsViaIntents,
  type GetCircularReferencesResult,
  type GetCellErrorsResult,
  deleteRangeViaIntents,
  insertRangeViaIntents,
  clearRangeViaIntents,
  ClearRangeOptions,
  ClearRangeClearType,
  sortRangeViaIntents,
  SortRangeKey,
  SortRangeOptions,
  applyFormattingViaIntents,
  ApplyFormattingItem,
  ApplyFormattingProperties,
  ApplyFormattingFont,
  ApplyFormattingInterior,
  ApplyFormattingBorder,
  ApplyFormattingBorders,
  moveRangeViaIntents,
  MoveRangeOptions,
  moveSheetViaIntents,
  MoveSheetOptions,
  MoveSheetResult,
  addWorksheetViaIntents,
  activateWorksheetViaIntents,
  deleteWorksheetViaIntents,
  renameWorksheetViaIntents,
  setWorksheetVisibilityViaIntents,
  type WorksheetVisibility,
  type SetWorksheetVisibilityOptions,
  addWorkbookViaIntents,
  copyWorksheetViaIntents,
  insertWorksheetsFromBase64ViaIntents,
  addActiveGraphViaIntents,
  type AddActiveGraphCoords,
  setActiveGraphXmlViaIntents,
  addExcelChartViaIntents,
  AddExcelChartOptions,
  AddExcelChartResult,
  updateExcelChartViaIntents,
  UpdateExcelChartOptions,
  UpdateExcelChartResult,
  addSeriesToChartViaIntents,
  AddSeriesToChartOptions,
  AddSeriesToChartResult,
  updateSeriesInChartViaIntents,
  UpdateSeriesInChartOptions,
  UpdateSeriesInChartResult,
  removeSeriesFromChartViaIntents,
  RemoveSeriesFromChartOptions,
  RemoveSeriesFromChartResult,
  getChartImageAsBase64ViaIntents,
  GetChartImageAsBase64Options,
  ChartImageFormat,
  addImageFromBase64ViaIntents,
  addNamedObjectViaIntents,
  AddNamedObjectOptions,
  hideColumnsViaIntents,
  hideRowsViaIntents,
  showColumnsViaIntents,
  showRowsViaIntents,
  groupColumnsViaIntents,
  groupRowsViaIntents,
  ungroupColumnsViaIntents,
  ungroupRowsViaIntents,
  freezeUnfreezePanesViaIntents,
  FreezeMode,
  FreezeUnfreezePanesOptions,
  deleteShapeViaIntents,
  DeleteShapeRef,
  modifyShapesViaIntents,
  ShapeModificationEntry,
  selectShapesViaIntents,
  SelectShapesOptions,
  selectRangeViaIntents,
  removeNamedObjectViaIntents,
  RemoveNamedObjectOptions,
  searchViaIntents,
  SpreadsheetSearchOptions,
  SpreadsheetSearchResult,
  getOpenWorkbooksViaIntents,
  openWorkbookViaIntents,
  saveWorkbookViaIntents,
  saveWorkbookAsViaIntents,
  type OpenWorkbookOptions,
  type OpenWorkbookResult,
  type SaveWorkbookResult,
  getWorkbookConnectionsViaIntents,
  getWorkbookNamedObjectsViaIntents,
  getRangeSnapshotViaIntents,
  getWorksheetsAsBase64ViaIntents,
  GetWorksheetsAsBase64Options,
  getPaneStatusViaIntents,
  GetPaneStatusOptions,
  PaneStatus,
  beginUndoViaIntents,
  endUndoViaIntents,
  getCommentViaIntents,
  setCommentViaIntents,
  deleteCommentViaIntents,
  getAllCommentsViaIntents,
  getNoteViaIntents,
  setNoteViaIntents,
  deleteNoteViaIntents,
  getAllNotesViaIntents,
  insertTtfApiResponseViaIntents,
  previewChartViaIntents,
  PreviewChartOptions,
  downloadExcelChartViaIntents,
  DownloadExcelChartOptions,
  downloadActiveGraphViaIntents,
  DownloadActiveGraphOptions,
  FactsetDownloadActiveGraph,
  getActiveGraphXmlViaIntents,
  GetActiveGraphXmlOptions,
  SetActiveGraphXmlOptions,
  getStylesetsViaIntents,
  GetStylesetsOptions,
  getTemplatesListViaIntents,
  GetTemplatesListOptions,
  getUserAvailableTemplatesViaIntents,
  GetUserAvailableTemplatesOptions,
  downloadTemplateViaIntents,
  DownloadTemplateOptions,
  refreshCalculateViaIntents,
  RefreshCalculateOptions,
  getRefreshStatusViaIntents,
  getRefreshViaIntents,
  pollRefreshCalculate,
  GetRefreshStatusOptions,
  GetRefreshOptions,
  PollRefreshCalculateOptions,
  FactsetGetRefreshStatusResult,
  SpreadsheetCellContent,
  ResizeAxis,
  ResizeItem,
  CalculationMode,
  ActiveExcelContext,
  RangeAddress,
  GetRangeInfoOptions,
  GetWorkbookSheetsOptions,
  HarmonizedRefreshOptions,
  ApplicationCalculateType,
  FormulaParsingLevel,
  GetChartsOptions,
  ChartingChartInfo,
  GetTablesOptions,
  DeleteRangeShift,
  InsertRangeOptions,
  AddWorksheetOptions,
  ActivateWorksheetOptions,
  DeleteWorksheetOptions,
  RenameWorksheetOptions,
  CopyWorksheetOptions,
  InsertWorksheetsFromBase64Options,
  AddImageFromBase64Options,
  OpenWorkbookInfo,
  WorkbookConnection,
  GetWorkbookNamedObjectsOptions,
  WorkbookNamedObject,
  SetCommentOptions,
  SetNoteOptions,
  GetAllAnnotationsOptions,
  SpreadsheetCellComment,
  SpreadsheetCommentEntry,
  SpreadsheetNoteEntry,
  BeginUndoOptions,
  TtfApiResponse,
  InsertTtfApiResponseOptions,
} from "./services/factset/spreadsheetIntents";
import type {
  FactsetChartParameters,
  FactsetChartPreview,
  FactsetDownloadExcelChart,
  FactsetGetStylesetsResult,
  FactsetGetActiveGraphXmlResult,
  FactsetGetTemplatesListResult,
  FactsetGetUserAvailableTemplatesResult,
  FactsetDownloadTemplateRequest,
  FactsetDownloadTemplate,
  FactsetRefreshCalculateRequest,
  FactsetRefreshCalculateResult,
  FactsetGetRefreshRequest,
  FactsetGetRefreshResult,
  CellFormat,
} from "./types";

// ============================================================================
// GATE
// ============================================================================

/**
 * Returns `true` when Excel host operations should be dispatched through the
 * FactSet intent bridge instead of Office.js. All three conditions must hold:
 *
 *  - Office.js is globally bypassed (feature flag on).
 *  - The window-level intent bridge is wired up.
 *  - The parent host has identified itself as a spreadsheet.
 */
export function shouldUseExcelIntentBridge(): boolean {
  return (
    isOfficeJsBypassed() &&
    isFactSetIntentBridgeAvailable() &&
    isFactSetSpreadsheetHost()
  );
}

// ============================================================================
// ADDRESS HELPERS
// ============================================================================

/**
 * Strip any `Sheet!`, `'Sheet Name'!`, `[Book.xlsx]Sheet!`, or
 * `'[Book.xlsx]My Sheet'!` prefix and any leading `$` anchors, returning
 * just the leaf cell/range portion. Uses `lastIndexOf("!")` so the
 * separator is always the rightmost `!`; embedded `!` inside a quoted
 * workbook/sheet token therefore parses correctly.
 */
function stripSheetPrefix(address: string): string {
  const bang = address.lastIndexOf("!");
  const bare = bang >= 0 ? address.slice(bang + 1) : address;
  return bare.replace(/\$/g, "");
}

/**
 * Return the top-left cell of a range address (e.g. `"A1:C4"` -> `"A1"`).
 * For a single-cell address, returns it verbatim after stripping prefixes.
 */
function topLeftOf(address: string): string {
  const bare = stripSheetPrefix(address);
  const colon = bare.indexOf(":");
  return colon >= 0 ? bare.slice(0, colon) : bare;
}

/**
 * Extract the sheet name from an address that may carry any of four
 * prefix shapes:
 *  - `Sheet1!A1` -> `Sheet1`
 *  - `'My Sheet'!A1` -> `My Sheet`
 *  - `[Other.xlsx]Sheet1!A1` -> `Sheet1`
 *  - `'[Other.xlsx]My Sheet'!A1` -> `My Sheet`
 *
 * Doubled apostrophes inside a quoted name are unescaped back to a
 * single apostrophe. Returns `undefined` when no `!` separator is
 * present (bare `A1` / `A1:C3`) or the prefix is empty after stripping.
 *
 * Important for the external-ref builder downstream: if the caller
 * hands us `'[Other.xlsx]My Sheet'!A1` and a NEW `workbookName` like
 * `Pinned.xlsx`, this helper drops the old `[Other.xlsx]` so the
 * external-ref builder produces `'[Pinned.xlsx]My Sheet'!A1` instead
 * of a double-bracketed garbage prefix.
 */
function sheetNameFromAddress(address: string): string | undefined {
  const bang = address.lastIndexOf("!");
  if (bang < 0) return undefined;
  let raw = address.slice(0, bang);
  if (raw.startsWith("'") && raw.endsWith("'") && raw.length >= 2) {
    raw = raw.slice(1, -1).replace(/''/g, "'");
  }
  if (raw.startsWith("[")) {
    const close = raw.indexOf("]");
    if (close >= 0) {
      raw = raw.slice(close + 1);
    }
  }
  return raw || undefined;
}

/**
 * Predicate for the sheet-only legacy `Sheet!A1` output. Preserves the
 * historical behavior of `destinationCellOf` when no workbook is in
 * play: any character outside `[A-Za-z0-9_]` forces quoting.
 */
function needsSheetQuoting(name: string): boolean {
  return /[^A-Za-z0-9_]/.test(name);
}

function quoteSheetNameForA1(sheetName: string): string {
  return needsSheetQuoting(sheetName)
    ? `'${sheetName.replace(/'/g, "''")}'`
    : sheetName;
}

/**
 * Normalize a caller-supplied workbook name into the form
 * `buildExternalRefDestination` expects. Returns `undefined` when the
 * input is absent or unusable:
 *
 *  - `trim()` whitespace; empty -> `undefined`.
 *  - Strip one surrounding bracket pair (`[Book.xlsx]` -> `Book.xlsx`)
 *    so a caller that already wrapped the token gets normalized.
 *  - Reject values containing `!` (caller likely passed a full address
 *    instead of a workbook name). Falls back to the legacy form with a
 *    `console.warn` so the misuse surfaces in DevTools without aborting
 *    the burst — host-side rejections still bubble up through the
 *    regular per-cell catch path.
 */
function normalizeWorkbookName(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  let candidate = trimmed;
  if (
    candidate.startsWith("[") &&
    candidate.endsWith("]") &&
    candidate.length >= 2
  ) {
    candidate = candidate.slice(1, -1);
  }
  if (candidate.includes("!")) {
    console.warn(
      `[excelHostOps] destinationCellOf: ignoring \`workbookName\` ` +
        `"${raw}" because it contains "!" (caller likely passed a full ` +
        `address instead of a workbook name). Falling back to the ` +
        `legacy sheet-qualified target.`
    );
    return undefined;
  }
  return candidate;
}

/**
 * Build the `'[Book.xlsx]Sheet'!A1` Excel external-reference form of
 * `destinationCell`. The quotes wrap the entire `[book]sheet` prefix once
 * and are emitted unconditionally; the workbook and sheet are never quoted
 * independently (`[Book.xlsx]'My Sheet'!A1` is a shape Excel rejects).
 * Apostrophes in either name are doubled.
 *
 * Always quoting is deliberate. This used to quote only when either name
 * contained a character outside a per-component allow-list, and the
 * workbook allow-list permitted `-` on the theory that ordinary filenames
 * carry hyphens (`Model-v2.xlsx`). They do — but a hyphen is an operator
 * character in a reference, so the resulting unquoted
 * `[ttf-error.xlsx]Sheet2!B2` is not a valid reference and the host
 * rejects the whole intent with `InvalidArgument: The argument is invalid
 * or missing or has an incorrect format.` That killed every cell and range
 * write into any workbook with a hyphenated filename, while writes to the
 * same workbook succeeded whenever the *sheet* name happened to contain a
 * space (which tripped the old predicate into quoting and incidentally
 * produced a valid reference). Redundant quoting is harmless — Excel and
 * the host both accept `'[Book.xlsx]Sheet1'!A1` — so quote every time and
 * the failure mode disappears with it.
 *
 * Exported for direct unit-testing; in production callers go through
 * `destinationCellOf`, which applies normalization first.
 */
export function buildExternalRefDestination(
  topLeftCell: string,
  sheetName: string,
  workbookName: string
): string {
  const escapedBook = workbookName.replace(/'/g, "''");
  const escapedSheet = sheetName.replace(/'/g, "''");
  return `'[${escapedBook}]${escapedSheet}'!${topLeftCell}`;
}

/**
 * Compose the `destinationCell` payload for an `InsertContentsToRange`
 * write. Three flavors, in order of preference:
 *
 *  1. **External ref** (`'[Book.xlsx]Sheet'!A1`) — when `workbookName`
 *     is present AND a sheet can be resolved from the address or
 *     `sheetName`. Address-embedded sheets win in this mode because
 *     `sheetName` may be only a fallback from the apply loop. Routes
 *     the write to a specific workbook without
 *     depending on the host's active-workbook focus, so plan-burst
 *     writes stay pinned even if the user clicks into another
 *     workbook mid-burst. (FactSet confirmed this routing on Slack;
 *     see also [`excel-tools.md` §`InsertContentsToRange`] which
 *     documents `destinationCell` as accepting full addresses.)
 *  2. **Sheet-qualified** (`Sheet!A1`) — when only `sheetName` is
 *     present (or extractable from the address). Today's legacy
 *     behavior.
 *  3. **Bare cell** (`A1`) — when neither workbook nor sheet is
 *     resolvable. Falls back to whichever sheet/workbook is active.
 *
 * Pinned writes with no resolvable sheet (workbook present, sheet
 * absent) cannot produce valid Excel external-ref syntax — `[book]!A1`
 * is rejected by the host. In that case we fall back to the bare-cell
 * form and `console.warn`. The apply layer is responsible for making
 * that fallback rare by passing a resolved sheet name (typically the
 * burst's reactivation `sheetHint`).
 *
 * Exported so unit tests can exercise the actual helper instead of
 * re-creating its private logic.
 */
export function destinationCellOf(
  address: string,
  sheetName?: string,
  workbookName?: string
): string {
  const topLeft = topLeftOf(address);
  const addressSheet = sheetNameFromAddress(address);
  const resolvedWorkbook = normalizeWorkbookName(workbookName);

  if (resolvedWorkbook) {
    const resolvedSheet = addressSheet ?? sheetName;
    if (!resolvedSheet) {
      console.warn(
        `[excelHostOps] destinationCellOf: \`workbookName\` ` +
          `"${resolvedWorkbook}" is set but no sheet name could be ` +
          `resolved from the address ("${address}") or caller. Excel ` +
          `external-ref syntax requires \`[book]sheet!cell\`, so ` +
          `falling back to the bare-cell form ("${topLeft}"). This ` +
          `write will land on whichever workbook is active at dispatch ` +
          `time. The apply layer should pass a resolved sheet to keep ` +
          `the pin effective.`
      );
      return topLeft;
    }
    return buildExternalRefDestination(topLeft, resolvedSheet, resolvedWorkbook);
  }

  const resolvedSheet = sheetName ?? addressSheet;
  return resolvedSheet
    ? `${quoteSheetNameForA1(resolvedSheet)}!${topLeft}`
    : topLeft;
}

/**
 * Coerce an arbitrary cell value into the string form expected by
 * `SpreadsheetCellContent.formula`. Strings pass through verbatim (so leading
 * `=` formulas and literal text are both preserved). Numbers, booleans, and
 * `null`/`undefined` are stringified in a spreadsheet-friendly way.
 */
function toFormulaString(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return String(value);
}

// ============================================================================
// CONTENT BUILDERS
// ============================================================================

/**
 * Build a one-entry `contents[]` payload targeting a single cell.
 *
 * The host anchors the write at `options.destinationCell` and treats
 * `contents[].row/col` as **0-based offsets** from that anchor (despite the
 * spec wording "1-based row/col index"). We verified this empirically:
 *   - `dest="A1"` + `row:1, col:1` → lands at B2
 *   - `dest="A1"` + `row:0, col:0` → lands at A1  ← what we want
 * Leaving `destinationCell` out appears to make the host drop the write
 * silently, so we always set it. See `spreadsheetIntents.ts` for the trap.
 */
export function buildContentsForSingleCell(
  address: string,
  value: string | number | boolean | null | undefined,
  numberFormat?: string,
  sheetName?: string,
  workbookName?: string
): { contents: SpreadsheetCellContent[]; destinationCell: string } {
  const destinationCell = destinationCellOf(address, sheetName, workbookName);
  return {
    destinationCell,
    contents: [
      {
        row: 0,
        col: 0,
        formula: toFormulaString(value),
        ...(numberFormat ? { numberFormat } : {}),
      },
    ],
  };
}

/**
 * Build a `contents[]` payload that covers the given range, mapping every
 * entry of `values2d` to a `{ row, col, formula }` triple. `row` and `col`
 * are **0-based offsets** from the range's top-left cell, which becomes the
 * `destinationCell` (see `buildContentsForSingleCell` for the anchoring
 * rationale).
 *
 * The payload is validated against the A1 target before any intent is built.
 * Model-authored content is never broadcast, padded, truncated, or partially
 * fanned out: malformed/ragged/mismatched matrices throw synchronously.
 *
 * The per-cell fan-out here is what an additive 2D `values`/`formulas`
 * entry shape would collapse — deferred until payload size is a
 * demonstrated bottleneck. See the `SpreadsheetCellContent` doc in
 * `spreadsheetIntents.ts` for the full rationale; revisit if large range
 * writes start showing meaningful latency.
 */
export function buildContentsForRange(
  address: string,
  values2d: (string | number | boolean | null | undefined)[][],
  numberFormat?: string,
  sheetName?: string,
  workbookName?: string
): { contents: SpreadsheetCellContent[]; destinationCell: string } {
  const target = parseCellRangeAddress(address);
  if (!target) {
    throw new Error(
      `Invalid range address "${address}". Expected a non-reversed A1 cell or rectangular range.`,
    );
  }
  if (!Array.isArray(values2d) || values2d.length === 0) {
    throw new Error(`Range ${target.normalizedAddress} requires a non-empty content matrix.`);
  }
  const columnCount = Array.isArray(values2d[0]) ? values2d[0].length : 0;
  if (columnCount === 0) {
    throw new Error(
      `Range ${target.normalizedAddress} requires non-empty matrix rows.`,
    );
  }
  for (let row = 0; row < values2d.length; row += 1) {
    if (!Array.isArray(values2d[row])) {
      throw new Error(`Range ${target.normalizedAddress} matrix row ${row} is not an array.`);
    }
    if (values2d[row].length !== columnCount) {
      throw new Error(
        `Range ${target.normalizedAddress} matrix is ragged: row ${row} has ` +
        `${values2d[row].length} columns, expected ${columnCount}.`,
      );
    }
  }
  if (
    values2d.length !== target.rowCount ||
    columnCount !== target.columnCount
  ) {
    throw new Error(
      `Range ${target.normalizedAddress} is ${target.rowCount}×${target.columnCount}, ` +
      `but the content matrix is ${values2d.length}×${columnCount}. ` +
      "Supply exactly one entry per target cell or narrow the address.",
    );
  }

  const destinationCell = destinationCellOf(address, sheetName, workbookName);
  diag("format", "range_contents_format", { address, numberFormat, rowCount: values2d.length, colCount: columnCount });
  const contents: SpreadsheetCellContent[] = [];
  for (let r = 0; r < values2d.length; r++) {
    const row = values2d[r] ?? [];
    for (let c = 0; c < row.length; c++) {
      contents.push({
        row: r,
        col: c,
        formula: toFormulaString(row[c]),
        ...(numberFormat ? { numberFormat } : {}),
      });
    }
  }
  return { destinationCell, contents };
}

// ============================================================================
// DISPATCHERS
// ============================================================================

/**
 * Per-call dispatcher options shared by `setCellContents` and
 * `fillRangeContents`.
 *
 *  - `numberFormat` — applied to every written cell.
 *  - `undoEntryText` — label in Excel's undo history.
 *  - `displayAlerts` — opt back in to Excel's native "There's already data
 *    here. Do you want to replace it?" modal. **Defaults to `false`**: the
 *    bridge is non-interactive, so the host-side `Application.DisplayAlerts
 *    = False` wrap is the standing policy. Matches the `DeleteWorksheet`
 *    precedent and closes AI-2615. Pass `true` only from interactive
 *    user-confirmation flows where you genuinely want the host modal.
 *  - `selectRange` — opt back in to the host scrolling to the just-written
 *    range. **Defaults to `false`**: the applier (`excelService.ts`) issues
 *    an explicit `factset.Spreadsheet.SelectRange` only when the planner
 *    emits a `select_range` action, so any implicit nav from a fill is
 *    just viewport noise (and was occasionally yanking the user out of an
 *    in-focus ActiveGraph).
 *  - `sheetName` — qualifies `options.destinationCell` as `Sheet!A1` so
 *    writes do not depend on whichever worksheet is active at dispatch time.
 *  - `workbookName` — pins the write to a specific workbook by embedding it
 *    in `destinationCell` as an Excel external reference
 *    (`'[Book.xlsx]Sheet1'!A1`). Set this in the plan loop alongside
 *    `sheetName` so cell writes stay routed to the run's origin workbook
 *    even if the user switches focus mid-burst — the burst-start
 *    `activateWorksheet({ workbookName })` reactivation only fixes the
 *    *first* call's routing, this is what fixes every subsequent call.
 *    Requires `sheetName` to take effect: Excel external-ref syntax is
 *    `[book]sheet!cell`, and `[book]!cell` is invalid; if no sheet is
 *    resolvable the helper warns and falls back to the bare-cell form.
 *    Omit for one-shot writes (chat-chart insert, ad-hoc) that should
 *    target the user's active workbook.
 */
export interface ExcelHostOpOptions {
  numberFormat?: string;
  undoEntryText?: string;
  displayAlerts?: boolean;
  selectRange?: boolean;
  sheetName?: string;
  workbookName?: string;
}

/**
 * Write a single value/formula to `address` via the Spreadsheet intent bridge.
 *
 * Throws if the bridge call fails. Callers that want a local-fallback path
 * should gate this with `shouldUseExcelIntentBridge()` and catch errors.
 *
 * Defaults `displayAlerts: false` and `selectRange: false` on the wire — see
 * `ExcelHostOpOptions` for the rationale. Callers that need the host modal
 * or implicit nav can flip either flag back to `true` explicitly.
 */
export async function setCellContents(
  address: string,
  value: string | number | boolean | null | undefined,
  options: ExcelHostOpOptions = {}
): Promise<void> {
  const { contents, destinationCell } = buildContentsForSingleCell(
    address,
    value,
    options.numberFormat,
    options.sheetName,
    options.workbookName
  );
  await insertContentsToRangeViaIntents(contents, {
    destinationCell,
    refresh: false,
    displayAlerts: options.displayAlerts ?? false,
    selectRange: options.selectRange ?? false,
    undoInfo: {
      trackUndo: true,
      undoEntryText: options.undoEntryText ?? "FactSet AI: set cell",
    },
  });
}

/**
 * Fill a range with a 2D array of values/formulas via the Spreadsheet intent
 * bridge. See `setCellContents` for throw/gating semantics.
 *
 * Same `displayAlerts: false` / `selectRange: false` Finster defaults as
 * `setCellContents` — see `ExcelHostOpOptions` for the rationale.
 */
export async function fillRangeContents(
  address: string,
  values2d: (string | number | boolean | null | undefined)[][],
  options: ExcelHostOpOptions = {}
): Promise<void> {
  const { contents, destinationCell } = buildContentsForRange(
    address,
    values2d,
    options.numberFormat,
    options.sheetName,
    options.workbookName
  );
  await insertContentsToRangeViaIntents(contents, {
    destinationCell,
    refresh: false,
    displayAlerts: options.displayAlerts ?? false,
    selectRange: options.selectRange ?? false,
    undoInfo: {
      trackUndo: true,
      undoEntryText: options.undoEntryText ?? "FactSet AI: fill range",
    },
  });
}

// ============================================================================
// READ DISPATCHERS
// ============================================================================

/**
 * Resolve the currently active cell's address via the Spreadsheet intent
 * bridge.
 *
 * Today the host returns a bare A1-style cell (e.g. `"B3"`) with no sheet
 * prefix — see `spreadsheetIntents.ts::getActiveCellAddressViaIntents` for
 * the spec-level explanation. Callers that need the active sheet name will
 * have to wait for the `sheetName` extension proposed in
 * `missing-intents-v0.md` §8.1 (or use a different intent).
 *
 * Throws if the bridge call fails. Callers that want a local-fallback path
 * should gate this with `shouldUseExcelIntentBridge()` and catch errors.
 */
export async function getActiveCellAddress(): Promise<string> {
  return getActiveCellAddressViaIntents();
}

/**
 * Resolve the active workbook / worksheet context via the Spreadsheet
 * intent bridge.
 *
 * Thin pass-through over `getActiveExcelContextViaIntents`. Returns the
 * typed `ActiveExcelContext` shape (active sheet, workbook name,
 * selection, busy / edit-mode flags). The legacy JSON-string-blob path
 * was removed on 2026-04-28 — `excel-tools.md` still flags this intent
 * Experimental; doc ask in `missing-intents-v0.md` §2.2.
 */
export async function getActiveExcelContext(): Promise<ActiveExcelContext> {
  return getActiveExcelContextViaIntents();
}

/**
 * Resolve the active worksheet's name via the Spreadsheet intent bridge.
 *
 * Convenience wrapper around `getActiveExcelContext()` for the common
 * "what sheet is the user on right now?" case (polled every 2s by the
 * sidebar). Callers that need index, workbook name, or the full sheet
 * list should use `getActiveExcelContext()` directly.
 */
export async function getActiveSheetName(): Promise<string> {
  const ctx = await getActiveExcelContext();
  return ctx.activeSheet.name;
}

// ============================================================================
// RESIZE DISPATCHERS
// ============================================================================

/**
 * Build the `resizeTarget` object for a single address + optional sheet +
 * optional workbook.
 *
 * Sheet prefixes in the address (e.g. `"Sheet1!A:C"`) are stripped off the
 * address field and promoted to `worksheetName` so the host doesn't have to
 * re-parse. When no sheet is supplied, the host defaults to the active sheet.
 *
 * `workbookName`, when provided, pins the resize to a specific workbook via
 * the `RangeReferenceAddress.workbookName` routing override that
 * `ResizeExcelElements` accepts (per `f2_docs/excel/excel-tools.md`), so a
 * run targeted at its origin workbook keeps resizing there even when the
 * user has switched focus. Omit to target the active workbook.
 */
function buildResizeTarget(
  address: string,
  sheetName?: string,
  workbookName?: string,
): ResizeItem["resizeTarget"] {
  const bang = address.lastIndexOf("!");
  const bareAddress = bang >= 0 ? address.slice(bang + 1) : address;
  const embeddedSheet = bang >= 0 ? address.slice(0, bang).replace(/^'|'$/g, "") : undefined;
  const resolvedSheet = sheetName ?? embeddedSheet;
  return {
    address: bareAddress.replace(/\$/g, ""),
    referenceType: "absolute",
    ...(resolvedSheet ? { worksheetName: resolvedSheet } : {}),
    ...(workbookName ? { workbookName } : {}),
  };
}

/**
 * Reduce a cell-style range to the column-letter or row-number form
 * the FactSet `ResizeExcelElements` intent appears to expect for
 * `resizeMode: "FIT"`.
 *
 * Empirical observation (from the captured examples in
 * `excel-docs/intent-details.md` lines 472–495 — a log of real
 * payloads, not a contracted schema): in the autofit case
 * `resizeTarget.address` is a column letter (`"A"` or `"A:C"`) when
 * `resizeAxis === "COL"` or a row number (`"1"` or `"1:5"`) when
 * `resizeAxis === "ROW"`. In our test workbook, calling the intent
 * with a cell-style range (`"A1:C5"`) appeared to no-op silently
 * rather than autofit, so we project to the axis-only form before
 * dispatching.
 *
 * Inputs already in reduced form (`"A"`, `"A:C"`, `"1"`, `"1:5"`) are
 * passed through unchanged. Anything else with a parseable A1
 * endpoint pair is reduced to the appropriate axis projection.
 * Sheet prefixes (`"Sheet1!"`) are preserved on the way out so the
 * caller's downstream `buildResizeTarget` can still split them off.
 *
 * Returns the original address unchanged when it cannot be parsed —
 * the host then surfaces a clearer error than we could synthesise.
 */
function reduceAddressForFitAxis(address: string, axis: ResizeAxis): string {
  const bang = address.lastIndexOf("!");
  const sheetPrefix = bang >= 0 ? address.slice(0, bang + 1) : "";
  const bare = (bang >= 0 ? address.slice(bang + 1) : address).replace(
    /\$/g,
    ""
  );

  // Already in reduced form: pure column letters (with optional ":")
  // for COL, or pure row numbers (with optional ":") for ROW.
  if (axis === "COL" && /^[A-Z]+(:[A-Z]+)?$/i.test(bare)) {
    return `${sheetPrefix}${bare.toUpperCase()}`;
  }
  if (axis === "ROW" && /^\d+(:\d+)?$/.test(bare)) {
    return `${sheetPrefix}${bare}`;
  }

  // Cell-style range: split on ":" and project each endpoint to the
  // requested axis. Single-cell inputs (`A1`) collapse to a single
  // letter / row.
  const parts = bare.split(":");
  const endpoints = parts.length === 1 ? [parts[0], parts[0]] : parts.slice(0, 2);
  const projected: string[] = [];
  for (const ep of endpoints) {
    const m = /^([A-Z]+)(\d+)$/i.exec(ep);
    if (!m) return address; // unparseable — let the host complain
    projected.push(axis === "COL" ? m[1].toUpperCase() : m[2]);
  }
  const reduced =
    projected[0] === projected[1] ? projected[0] : `${projected[0]}:${projected[1]}`;
  return `${sheetPrefix}${reduced}`;
}

/**
 * Auto-fit rows or columns in the given range via the Spreadsheet intent
 * bridge.
 *
 * 1:1 replacement for `range.format.autofitColumns()` (`axis: "COL"`) and
 * `range.format.autofitRows()` (`axis: "ROW"`). See
 * `missing-intents-v0.md` §5.1 / §10 for the mapping.
 *
 * Uses `resizeMode: "FIT"` (fit to content) rather than `"AUTO"`. `"AUTO"`
 * is a host-internal heuristic that does not correspond to Office.js
 * autofit; `"FIT"` is the correct mode for "resize to contents". See
 * `ResizeMode` in `spreadsheetIntents.ts` for details.
 *
 * Throws if the bridge call fails. Callers that want a local-fallback path
 * should gate this with `shouldUseExcelIntentBridge()` and catch errors.
 */
export async function autofitRange(
  address: string,
  axis: ResizeAxis,
  sheetName?: string,
  workbookName?: string,
): Promise<void> {
  // `ResizeExcelElements` with `resizeMode: "FIT"` expects a
  // column-letter (`A:C`) or row-number (`1:5`) address — cell-style
  // ranges silently no-op on the host. See `reduceAddressForFitAxis`.
  const reduced = reduceAddressForFitAxis(address, axis);
  await resizeExcelElementsViaIntents([
    {
      resizeTarget: buildResizeTarget(reduced, sheetName, workbookName),
      resizeMode: "FIT",
      resizeAxis: axis,
    },
  ]);
}

/**
 * Set an explicit row height or column width via the Spreadsheet intent
 * bridge. `absoluteSize` is in the host's native unit (points for row
 * height, Excel column-width units for column width).
 */
export async function resizeAbsolute(
  address: string,
  axis: ResizeAxis,
  absoluteSize: number,
  sheetName?: string,
  workbookName?: string,
): Promise<void> {
  await resizeExcelElementsViaIntents([
    {
      resizeTarget: buildResizeTarget(address, sheetName, workbookName),
      resizeMode: "ABSOLUTE",
      resizeAxis: axis,
      absoluteSize,
    },
  ]);
}

// ============================================================================
// CALCULATION MODE DISPATCHER
// ============================================================================

/**
 * Flip the workbook's calculation mode via `factset.Spreadsheet.SetCalculationMode`.
 *
 * Intended for bracketing a burst of mutating intents with
 * `"manual"` → work → `"automatic"` so every intermediate write doesn't pay
 * for recalc. Callers should wrap both sides in `try/catch` and swallow
 * errors — a calc-mode flip failing must never break the write path.
 */
export async function setCalculationMode(mode: CalculationMode): Promise<void> {
  return setCalculationModeViaIntents(mode);
}

// ============================================================================
// RANGE READ DISPATCHERS
// ============================================================================

/**
 * Build a `RangeAddress` from an Excel-style address string + optional
 * sheet hint + optional workbook hint. Sheet prefixes embedded in the
 * address (`"Sheet1!A1:C5"`) are promoted to `worksheetName` so the
 * host doesn't have to re-parse; leading `$` anchors are stripped.
 *
 * `workbookName`, when provided, lets a plan run targeted at the
 * workbook it started in, even when the user has switched focus to a
 * different workbook mid-plan. The host treats the `workbookName`
 * field on `RangeAddress` as a routing override; intents that accept
 * `RangeAddress` (`GetRangeInfo`, `GetRangeAsCsv`, `ClearRange`,
 * `ApplyFormatting`, `ParseFormulas`, etc.) all honour it.
 *
 * Always emits `referenceType: "absolute"` — Finster doesn't use the
 * relative offset shape today.
 */
function buildRangeAddress(
  address: string,
  sheetName?: string,
  workbookName?: string,
): RangeAddress {
  const bang = address.lastIndexOf("!");
  const bareAddress = bang >= 0 ? address.slice(bang + 1) : address;
  const embeddedSheet =
    bang >= 0 ? address.slice(0, bang).replace(/^'|'$/g, "") : undefined;
  const resolvedSheet = sheetName ?? embeddedSheet;
  return {
    address: bareAddress.replace(/\$/g, ""),
    referenceType: "absolute",
    ...(resolvedSheet ? { worksheetName: resolvedSheet } : {}),
    ...(workbookName ? { workbookName } : {}),
  };
}

/**
 * Read values, formulas, and metadata for a range via the Spreadsheet
 * intent bridge. Pass `address` as a plain A1-style string (with optional
 * `Sheet!` prefix) and an optional `options` filter — the dispatcher
 * builds the absolute `RangeAddress` for you.
 *
 * Returns the raw `rangeInfo` payload; callers narrow at the use site.
 */
export async function getRangeInfo(
  address: string,
  options?: GetRangeInfoOptions,
  sheetName?: string,
  workbookName?: string,
): Promise<unknown> {
  return getRangeInfoViaIntents(
    buildRangeAddress(address, sheetName, workbookName),
    options,
  );
}

/**
 * Export a range as a CSV string via the Spreadsheet intent bridge. Used
 * as a fallback for `getRangeInfo` when the latter returns no data.
 */
export async function getRangeAsCsv(
  address: string,
  options?: string,
  sheetName?: string,
  workbookName?: string,
): Promise<string> {
  return getRangeAsCsvViaIntents(
    buildRangeAddress(address, sheetName, workbookName),
    options,
  );
}

/**
 * List sheets in a workbook via the Spreadsheet intent bridge. Pass
 * `workbookName: undefined` (or omit) to target the active workbook.
 */
export async function getWorkbookSheets(
  workbookName?: string,
  options?: GetWorkbookSheetsOptions
): Promise<unknown> {
  return getWorkbookSheetsViaIntents(workbookName, options);
}

/**
 * Resolve the currently selected range's address (e.g. `"B2:D5"`) via
 * the Spreadsheet intent bridge.
 */
export async function getSelectedAddressRange(): Promise<string> {
  return getSelectedAddressRangeViaIntents();
}

// ============================================================================
// REFRESH + CALCULATION DISPATCHERS
// ============================================================================

/**
 * Refresh one or more worksheets via the Spreadsheet intent bridge. Pass
 * an empty/undefined options object to refresh the currently selected
 * sheet(s) with default options (the wrapper sends `fdc3.nothing`). Per
 * FactSet's `excel-tools.md`, `fdc3.nothing` refreshes the currently
 * selected sheet(s) — NOT the entire workbook; pass an explicit
 * `sheetNames` list to refresh other tabs. Used to replace the legacy
 * export-and-download FDS refresh path in bridge mode.
 *
 * `timeoutMs` overrides the default 15s wrapper timeout. Pass a
 * generous value (e.g. 60000) on freshly-opened templates with many
 * FDS calls — the FactSet side fetch can take tens of seconds on
 * comps / valuation models, and the default would surface a spurious
 * timeout for refreshes that are actually progressing fine.
 */
export async function refreshWorksheets(
  options?: HarmonizedRefreshOptions,
  timeoutMs?: number
): Promise<string> {
  return harmonizedRefreshWorksheetsViaIntents(options, timeoutMs);
}

/**
 * Trigger a workbook recalculation via the Spreadsheet intent bridge.
 * Useful inside a manual calc bracket to make the write batch observable
 * before flipping the mode back to `"automatic"`.
 */
export async function applicationCalculate(
  calculationType?: ApplicationCalculateType
): Promise<string> {
  return applicationCalculateViaIntents(calculationType);
}

/**
 * Read the current workbook calculation mode via the Spreadsheet intent
 * bridge. Used inside the manual calc bracket to log the actual mode
 * (vs. the requested mode) for diagnostics.
 */
export async function getCalculationMode(): Promise<CalculationMode> {
  return getCalculationModeViaIntents();
}

// ============================================================================
// FORMULA PARSING DISPATCHER
// ============================================================================

/**
 * Parse formulas in a range via the Spreadsheet intent bridge. Used as a
 * pre-pass before the verifier so the LLM can reason about the formula
 * structure of the just-written range, not just the raw text.
 */
export async function parseFormulas(
  address: string,
  parsingLevel?: FormulaParsingLevel,
  sheetName?: string,
  workbookName?: string,
): Promise<unknown> {
  return parseFormulasViaIntents(
    buildRangeAddress(address, sheetName, workbookName),
    parsingLevel,
  );
}

// ============================================================================
// CHART / TABLE READ DISPATCHERS
// ============================================================================
//
// `GetCharts` (now `Charting.GetCharts`) returns a typed
// `ChartingChartInfo[]` with id / position / chartType / axes / series
// per chart; `GetTables` still returns an opaque typed array. Callers
// should `try/catch` + `console.warn` and treat these as best-effort
// enrichment, not required pre-conditions. `excel-tools.md` still flags
// both Experimental — doc ask in `missing-intents-v0.md` §2.2.

/**
 * List charts in a workbook via the Spreadsheet intent bridge
 * (`factset.Spreadsheet.Charting.GetCharts`). Returns the typed
 * `ChartingChartInfo[]` payload. Caller should `try/catch` and
 * `console.warn` on failure so a slow / failing read never blocks the
 * primary pipeline.
 */
export async function getCharts(
  options?: GetChartsOptions
): Promise<ChartingChartInfo[]> {
  return getChartsViaIntents(options);
}

/**
 * List tables in a workbook via the Spreadsheet intent bridge.
 * Same shape and warn-on-failure guidance as `getCharts`.
 */
export async function getTables(options?: GetTablesOptions): Promise<unknown[]> {
  return getTablesViaIntents(options);
}

/**
 * Read circular-reference metadata through the Spreadsheet intent bridge.
 *
 * @param workbookName - Optional workbook target.
 * @param signal - Optional caller cancellation signal.
 * @returns Iterative-calculation settings and circular-reference locations.
 * @throws When the bridge request fails or is cancelled.
 */
export async function getCircularReferences(
  workbookName?: string,
  signal?: AbortSignal
): Promise<GetCircularReferencesResult> {
  return getCircularReferencesViaIntents(workbookName, undefined, signal);
}

/**
 * Read workbook cell errors through the Spreadsheet intent bridge.
 *
 * @param workbookName - Optional workbook target.
 * @param otherErrors - Additional host-specific markers to scan.
 * @param signal - Optional caller cancellation signal.
 * @returns Normalized error groups for each worksheet.
 * @throws When the bridge request fails or is cancelled.
 */
export async function getCellErrors(
  workbookName?: string,
  otherErrors?: readonly string[],
  signal?: AbortSignal
): Promise<GetCellErrorsResult> {
  return getCellErrorsViaIntents(
    workbookName,
    otherErrors,
    undefined,
    signal
  );
}

// ============================================================================
// RANGE MUTATION DISPATCHERS
// ============================================================================

/**
 * Delete a range and shift surrounding cells via the Spreadsheet intent
 * bridge. Pass a plain A1-style address (with optional `Sheet!` prefix);
 * the dispatcher builds the absolute `RangeAddress` for you. `shift`
 * mirrors the host's `Shift{Left,Up}` enum and defaults to the host's
 * own choice when omitted.
 *
 * Throws if the bridge call fails. Callers that want a local-fallback
 * path should gate this with `shouldUseExcelIntentBridge()` and catch.
 */
export async function deleteRange(
  address: string,
  shift?: DeleteRangeShift,
  sheetName?: string,
  workbookName?: string,
): Promise<void> {
  await deleteRangeViaIntents(
    buildRangeAddress(address, sheetName, workbookName),
    shift,
  );
}

/**
 * Insert blank cells into a range and shift existing cells via the
 * Spreadsheet intent bridge. See `deleteRange` for the address-building
 * conventions; `options.shift` mirrors the host's `Shift{Right,Down}`
 * enum and `options.copyOrigin` controls which adjacent cells supply
 * formatting for the new range.
 */
export async function insertRange(
  address: string,
  options?: InsertRangeOptions,
  sheetName?: string,
  workbookName?: string,
): Promise<void> {
  await insertRangeViaIntents(
    buildRangeAddress(address, sheetName, workbookName),
    options,
  );
}

/**
 * Clear contents / formats / etc. from a range via the Spreadsheet intent
 * bridge. Pure in-place clear: cells stay, surrounding cells don't shift —
 * use `deleteRange` for the remove-and-shift semantics.
 *
 * `options.clearType` defaults to the host's `"All"` when omitted (clears
 * every property family in one pass). See `ClearRangeClearType` for the
 * full enum.
 *
 * Reachable via `RangeModification.action = "clear"` with optional
 * `clear.clearType` payload. Throws if the bridge call fails.
 */
export async function clearRange(
  address: string,
  options?: ClearRangeOptions,
  sheetName?: string,
  workbookName?: string,
): Promise<void> {
  await clearRangeViaIntents(
    buildRangeAddress(address, sheetName, workbookName),
    options,
  );
}

/**
 * Sort a non-Pivot-Table range via the Spreadsheet intent bridge.
 *
 * `sortKeys` carries 1–3 entries — each `{ key, order?, dataOption? }` — where
 * `key` is a cell reference inside the sort column or row (e.g. `"B1"` for
 * the first column of `A1:D20`); no sheet prefix on `key` itself. The
 * wrapper rejects empty / >3-entry arrays before the host call. `options`
 * exposes the host's `header` / `matchCase` / `orientation` / `sortMethod`
 * knobs verbatim.
 *
 * Reachable via `RangeModification.action = "sort"` with `sort.sortKeys` and
 * optional `sort.options.{header,matchCase,orientation,sortMethod}`. Throws
 * if the bridge call fails or the input fails the wrapper's own validation.
 */
export async function sortRange(
  address: string,
  sortKeys: SortRangeKey[],
  options?: SortRangeOptions,
  sheetName?: string,
  workbookName?: string,
): Promise<void> {
  await sortRangeViaIntents(
    buildRangeAddress(address, sheetName, workbookName),
    sortKeys,
    options
  );
}

// ============================================================================
// FORMAT TRANSLATION (planner CellFormat → wire ApplyFormattingProperties)
// ============================================================================

/**
 * Excel `XlUnderlineStyle` constants. Mirrors the host's COM enum so the
 * planner-friendly `"single"` / `"double"` / `"none"` strings round-trip
 * to the same wire values the FactSet host expects on `font.underline`.
 */
const UNDERLINE_TO_XL: Readonly<Record<NonNullable<CellFormat["font"]>["underline"] & string, number>> = {
  none: -4142,
  single: 2,
  double: -4119,
};

/**
 * Excel `XlLineStyle` + `XlBorderWeight` pairs for each planner border
 * style. Both fields are emitted on every border so the host doesn't pick
 * up a stale weight from a previously-applied border on the same edge.
 *
 * Constants:
 *   - lineStyle: `1` = continuous, `-4115` = dash, `-4118` = dot,
 *     `-4119` = double, `-4142` = none.
 *   - weight: `1` = hairline, `2` = thin, `-4138` = medium, `4` = thick.
 */
const BORDER_STYLE_TO_XL: Readonly<
  Record<NonNullable<NonNullable<CellFormat["borders"]>["top"]>["style"] & string, { lineStyle: number; weight: number }>
> = {
  none: { lineStyle: -4142, weight: 2 },
  thin: { lineStyle: 1, weight: 2 },
  medium: { lineStyle: 1, weight: -4138 },
  thick: { lineStyle: 1, weight: 4 },
  dashed: { lineStyle: -4115, weight: 2 },
  dotted: { lineStyle: -4118, weight: 2 },
  double: { lineStyle: -4119, weight: 2 },
};

/**
 * Excel `XlHAlign` constants for each planner horizontal-alignment value.
 */
const HORIZONTAL_ALIGN_TO_XL: Readonly<
  Record<NonNullable<NonNullable<CellFormat["alignment"]>["horizontal"]>, number>
> = {
  left: -4131,
  center: -4108,
  right: -4152,
  fill: 5,
  justify: -4130,
};

/**
 * Excel `XlVAlign` constants for each planner vertical-alignment value.
 */
const VERTICAL_ALIGN_TO_XL: Readonly<
  Record<NonNullable<NonNullable<CellFormat["alignment"]>["vertical"]>, number>
> = {
  top: -4160,
  center: -4108,
  bottom: -4107,
};

/**
 * Convert a hex `"#RRGGBB"` color (also accepts `"RRGGBB"` without the
 * leading `#`, plus a small set of named colours mirrored from
 * `excelService.ts::sanitizeColor`) into Excel's BGR-encoded 24-bit
 * integer used by `Range.Interior.Color` / `Range.Font.Color` /
 * `Border.Color`. Returns `undefined` for falsy or unparseable input so
 * the caller can omit the field entirely.
 */
function hexColorToBgr(color: string | undefined): number | undefined {
  if (!color) return undefined;
  const named: Record<string, string> = {
    black: "#000000",
    white: "#FFFFFF",
    red: "#FF0000",
    green: "#00FF00",
    blue: "#0000FF",
    yellow: "#FFFF00",
    gray: "#808080",
    grey: "#808080",
    orange: "#FFA500",
    purple: "#800080",
  };
  const normalized = color.toLowerCase().replace(/\s/g, "");
  const resolved = named[normalized] ?? color;
  const match = /^#?([0-9A-Fa-f]{6})$/.exec(resolved);
  if (!match) return undefined;
  const r = parseInt(match[1].slice(0, 2), 16);
  const g = parseInt(match[1].slice(2, 4), 16);
  const b = parseInt(match[1].slice(4, 6), 16);
  return (b << 16) | (g << 8) | r;
}

/**
 * Translate a planner-shaped `CellFormat` into the wire-shaped
 * `ApplyFormattingProperties` accepted by `factset.Spreadsheet.ApplyFormatting`.
 *
 * Returns `undefined` when the input has no field the wire intent can
 * carry — callers should skip the host call entirely in that case rather
 * than send an empty `formatting: {}` (the host treats that as a no-op
 * but still spends the round-trip).
 *
 * Field-by-field mapping:
 *   - `font.{name,size,bold,italic,strikethrough}` → wire verbatim.
 *   - `font.color` (hex `#RRGGBB`) → wire BGR int via `hexColorToBgr`.
 *   - `font.underline` (`"none"|"single"|"double"`) → `XlUnderlineStyle`.
 *   - `fill.color` (hex) → `interior.color` (BGR int) + `interior.pattern: 1` (solid).
 *   - `fill.pattern: "none"` → `interior.pattern: -4142` (xlPatternNone).
 *   - `borders.{top,bottom,left,right}.{color,style}` → `borders.edge*.{color,lineStyle,weight}`.
 *   - `alignment.{horizontal,vertical,wrapText,indent,textRotation}` → top-level wire fields.
 *   - `numberFormat` (string or `{ format }`) → `numberFormat` string.
 *   - `merge` is NOT carried — the wire intent has no merge counterpart;
 *     callers that need merge must take a separate path.
 */
export function cellFormatToWireFormatting(
  format: CellFormat | undefined
): ApplyFormattingProperties | undefined {
  if (!format) return undefined;
  const out: ApplyFormattingProperties = {};

  if (format.font) {
    const font: ApplyFormattingFont = {};
    if (format.font.name !== undefined) font.name = format.font.name;
    if (typeof format.font.size === "number" && format.font.size > 0) {
      font.size = format.font.size;
    }
    if (format.font.bold !== undefined) font.bold = format.font.bold;
    if (format.font.italic !== undefined) font.italic = format.font.italic;
    if (format.font.strikethrough !== undefined) {
      font.strikethrough = format.font.strikethrough;
    }
    const fontColor = hexColorToBgr(format.font.color);
    if (fontColor !== undefined) font.color = fontColor;
    if (format.font.underline) {
      font.underline = UNDERLINE_TO_XL[format.font.underline];
    }
    if (Object.keys(font).length > 0) out.font = font;
  }

  if (format.fill) {
    const interior: ApplyFormattingInterior = {};
    if (format.fill.pattern === "none") {
      interior.pattern = -4142;
    } else {
      const fillColor = hexColorToBgr(format.fill.color);
      if (fillColor !== undefined) {
        interior.color = fillColor;
        interior.pattern = 1;
      }
    }
    if (Object.keys(interior).length > 0) out.interior = interior;
  }

  if (format.borders) {
    const wireBorders: ApplyFormattingBorders = {};
    const slots: { src: keyof NonNullable<CellFormat["borders"]>; dst: keyof ApplyFormattingBorders }[] = [
      { src: "top", dst: "edgeTop" },
      { src: "bottom", dst: "edgeBottom" },
      { src: "left", dst: "edgeLeft" },
      { src: "right", dst: "edgeRight" },
    ];
    for (const { src, dst } of slots) {
      const edge = format.borders[src];
      if (!edge) continue;
      const wireEdge: ApplyFormattingBorder = {};
      const edgeColor = hexColorToBgr(edge.color);
      if (edgeColor !== undefined) wireEdge.color = edgeColor;
      if (edge.style) {
        const xl = BORDER_STYLE_TO_XL[edge.style];
        if (xl) {
          wireEdge.lineStyle = xl.lineStyle;
          wireEdge.weight = xl.weight;
        }
      }
      if (Object.keys(wireEdge).length > 0) wireBorders[dst] = wireEdge;
    }
    if (Object.keys(wireBorders).length > 0) out.borders = wireBorders;
  }

  if (format.alignment) {
    if (format.alignment.horizontal) {
      out.horizontalAlignment = HORIZONTAL_ALIGN_TO_XL[format.alignment.horizontal];
    }
    if (format.alignment.vertical) {
      out.verticalAlignment = VERTICAL_ALIGN_TO_XL[format.alignment.vertical];
    }
    if (format.alignment.wrapText !== undefined) {
      out.wrapText = format.alignment.wrapText;
    }
    if (typeof format.alignment.indent === "number") {
      out.indentLevel = Math.max(0, Math.min(250, format.alignment.indent));
    }
    if (typeof format.alignment.textRotation === "number") {
      out.orientation = Math.max(-90, Math.min(90, format.alignment.textRotation));
    }
  }

  if (format.numberFormat) {
    const nf =
      typeof format.numberFormat === "string"
        ? format.numberFormat
        : format.numberFormat.format;
    if (typeof nf === "string" && nf.length > 0) out.numberFormat = nf;
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

// ============================================================================
// FORMAT DISPATCHERS
// ============================================================================

/**
 * One range to format in an `applyFormatting()` batch. `address` is a
 * plain A1-style string (with optional `Sheet!` prefix); the dispatcher
 * builds the absolute `RangeAddress` for you. `format` is the planner
 * schema's `CellFormat` and is translated into the wire shape internally.
 */
export interface ApplyFormattingDispatch {
  address: string;
  format: CellFormat;
  sheetName?: string;
  /**
   * Pins the format to a specific workbook via `RangeAddress.workbookName`
   * (honoured by `ApplyFormatting` per `f2_docs/excel/excel-tools.md`). Omit
   * to target the active workbook.
   */
  workbookName?: string;
}

/**
 * Apply formatting (font / fill / border / alignment / number-format /
 * sizing) to one or more ranges via the Spreadsheet intent bridge.
 *
 * Inputs that translate to an empty `formatting` object (e.g. only
 * `merge: true`, which the wire intent doesn't carry) are silently
 * skipped — `merge` callers need a separate code path.
 *
 * The host returns an `errors[]` array; this dispatcher passes it back
 * verbatim so the call site can decide whether a non-empty `errors`
 * is fatal or surfaces as a `trackOperation` warning.
 *
 * Reachable via `RangeModification.action = "format"` and as a
 * post-fill format pass when `RangeModification` / `CellModification`
 * carries a `format` payload alongside a `"fill"` / `"set"` action.
 */
export async function applyFormatting(
  items: ApplyFormattingDispatch[]
): Promise<unknown[]> {
  const wireItems: ApplyFormattingItem[] = [];
  for (const item of items) {
    const formatting = cellFormatToWireFormatting(item.format);
    if (!formatting) continue;
    wireItems.push({
      range: buildRangeAddress(item.address, item.sheetName, item.workbookName),
      formatting,
    });
  }
  if (wireItems.length === 0) return [];
  return applyFormattingViaIntents(wireItems);
}

/**
 * Move (or copy, when `options.preserveSource = true`) the cells of
 * `source` to `destination` via the Spreadsheet intent bridge.
 *
 *  - `source` is a plain A1-style range (e.g. `"A1:B5"`); the dispatcher
 *    builds the absolute `RangeAddress` for you.
 *  - `destination` is the top-left anchor cell of the moved block
 *    (e.g. `"D1"`) — the host expands it to match `source`'s shape.
 *  - `sourceSheetName` / `destinationSheetName` thread sheet targeting
 *    onto each side independently, so cross-sheet moves work in one
 *    call. Omit either to default to the active sheet.
 *  - `options.preserveSource: true` switches from cut-and-paste (default)
 *    to copy semantics.
 *
 * Reachable via `RangeModification.action = "moveRange"` with
 * `moveRange.{destination, preserveSource?, sourceSheetName?, destinationSheetName?}`.
 * Throws if the bridge call fails or the host reports `success=false`.
 */
export async function moveRange(
  source: string,
  destination: string,
  options?: MoveRangeOptions,
  sourceSheetName?: string,
  destinationSheetName?: string,
  workbookName?: string,
): Promise<void> {
  // In-workbook moves: pin both sides to the same workbook so the host
  // routes them to the run's origin workbook even when the user has
  // switched focus. `MoveRange` honours `workbookName` on both `source`
  // and `destination` per `f2_docs/excel/excel-tools.md`.
  await moveRangeViaIntents(
    buildRangeAddress(source, sourceSheetName, workbookName),
    buildRangeAddress(destination, destinationSheetName, workbookName),
    options
  );
}

// ============================================================================
// WORKBOOK / WORKSHEET MUTATION DISPATCHERS
// ============================================================================

/**
 * Add a new worksheet via the Spreadsheet intent bridge. When `options`
 * carries any field, the host honours name / index / target-workbook /
 * activate-after-create. When `options` is undefined or empty, this
 * dispatches the legacy parameterless form (host-default name, append
 * at end, active workbook).
 *
 * **Catalog status (2026-04-29 refresh):** the public `excel-tools.md`
 * `AddWorksheet` row still describes only the legacy `fdc3.nothing`
 * input context — the typed `factset.spreadsheet.addWorksheet` shape
 * this dispatcher relies on is accepted on the wire but **not yet
 * documented in the public catalog**. Tracked in `missing-intents-v0.md`
 * §2.2 + `#open-addworksheet-params`.
 *
 * **Indexing note.** `options.index` is currently **1-based** on the wire
 * (legacy with `getActiveExcelContext` / `getWorkbookSheets`). FactSet has
 * pre-announced a 0-based migration for `addWorksheet`, `getActiveExcelContext`,
 * `getWorkbookSheets`, `getTables`, and `getCharts`. When that lands the
 * field semantics flip; this dispatcher is a pass-through so the change is
 * doc-only here. See `missing-intents-v0.md` → `#open-zero-index-migration`.
 */
export async function addWorksheet(
  options?: AddWorksheetOptions
): Promise<string> {
  return addWorksheetViaIntents(options);
}

/**
 * Activate a worksheet by name via the Spreadsheet intent bridge.
 * `options.workbookName` targets a non-active workbook; omit to operate
 * on the active workbook.
 */
export async function activateWorksheet(
  worksheetName: string,
  options?: ActivateWorksheetOptions
): Promise<void> {
  await activateWorksheetViaIntents(worksheetName, options);
}

/**
 * Delete a worksheet by name via the Spreadsheet intent bridge.
 * `options.displayAlerts` defaults to `false` so the host doesn't block
 * waiting for the user to dismiss its "are you sure?" modal — Finster is
 * non-interactive.
 */
export async function deleteWorksheet(
  worksheetName: string,
  options?: DeleteWorksheetOptions
): Promise<void> {
  await deleteWorksheetViaIntents(worksheetName, options);
}

/**
 * Rename a worksheet via the Spreadsheet intent bridge. The host applies
 * Excel naming rules (31-char cap, no `: \ / ? * [ ]`) and may sanitise
 * `newName` rather than rejecting it; read the post-rename sheet list
 * via `getWorkbookSheets` if the applied name matters.
 */
export async function renameWorksheet(
  worksheetName: string,
  newName: string,
  options?: RenameWorksheetOptions
): Promise<void> {
  await renameWorksheetViaIntents(worksheetName, newName, options);
}

/**
 * Show / hide / very-hide a worksheet via the Spreadsheet intent
 * bridge.
 *
 * **Cataloged 2026-05-12 (PR #55)** — per-sheet sibling of the
 * per-row / per-column `hideRows` / `hideColumns` / `showRows` /
 * `showColumns` family. `"VeryHidden"` removes the sheet from the
 * Excel UI entirely; only another programmatic call to `"Visible"`
 * brings it back. Use `"Hidden"` when the user should be able to
 * unhide via the ribbon.
 *
 * `worksheetName` is **optional** (host change 2026-05-12) — omit to
 * operate on the active worksheet. Pass an explicit name to target a
 * specific sheet.
 *
 * Office.js fallback is intentionally **not** wired — bridge-only
 * for now, matching the rest of the post-2026-05-06 chart/shape
 * wave. If we ever sideload outside FactSet we can add an
 * `Excel.SheetVisibility.{visible|hidden|veryHidden}` fallback under
 * the same try/await/sync pattern as `hideColumns`.
 *
 * Reachable from the planner via
 * `SheetModification.action = "setVisibility"` with the `visibility`
 * field; `name` is optional and falls back to the active sheet.
 */
export async function setWorksheetVisibility(
  worksheetName: string | undefined,
  visibility: WorksheetVisibility,
  options?: SetWorksheetVisibilityOptions
): Promise<void> {
  await setWorksheetVisibilityViaIntents(worksheetName, visibility, options);
}

export type { WorksheetVisibility, SetWorksheetVisibilityOptions };

/**
 * Move (reorder) a worksheet via the Spreadsheet intent bridge.
 *
 * `newIndex` is **0-based** on the destination workbook (the leftmost
 * tab is `0`). Pass `options.worksheetName` to target a specific sheet;
 * omit it to move the active sheet. Set both `sourceWorkbookName` and
 * `targetWorkbookName` (to different values) for a cross-workbook move
 * — the source sheet is removed from `sourceWorkbookName` and inserted
 * into `targetWorkbookName` at `newIndex`. `selectSheet: true` activates
 * the moved sheet at its new home.
 *
 * The host returns the actually-applied indices and workbook names; we
 * pass them back so callers can surface drift (e.g. when `newIndex` got
 * clamped or the host normalised the workbook name).
 *
 * Reachable via `SheetModification.action = "move"` with the `move`
 * sub-object; throws if the bridge call fails or the host reports
 * `success=false`.
 */
export async function moveSheet(
  newIndex: number,
  options?: MoveSheetOptions
): Promise<MoveSheetResult> {
  return moveSheetViaIntents(newIndex, options);
}

/**
 * Add a new workbook via the Spreadsheet intent bridge.
 *
 * **Return value is a host status message, NOT a workbook name.** The
 * intent reply's `message` field is a human-readable string like
 * `"Workbook added successfully"`. Do not feed this string to follow-up
 * intents that expect a workbook identifier (e.g. as a `workbookName` in
 * subsequent options). The new workbook becomes the active workbook on
 * the host side, so subsequent intents that target the active workbook
 * implicitly land on it; if you need its actual name, follow up with a
 * separate `getActiveExcelContext` / `getOpenWorkbooks` call.
 */
export async function addWorkbook(): Promise<string> {
  return addWorkbookViaIntents();
}

/**
 * Copy a worksheet via the Spreadsheet intent bridge. Pass `undefined`
 * (or an empty options object) to copy the active sheet with the host's
 * default placement.
 */
export async function copyWorksheet(
  options?: CopyWorksheetOptions
): Promise<unknown> {
  return copyWorksheetViaIntents(options);
}

/**
 * Insert worksheets from a base64-encoded `.xlsx` file via the
 * Spreadsheet intent bridge. Returns the list of inserted sheet names.
 *
 * Pass `{ workbookName }` to target a specific workbook instead of whichever
 * one happens to be active.
 */
export async function insertWorksheetsFromBase64(
  base64File: string,
  options?: InsertWorksheetsFromBase64Options,
  timeoutMs?: number
): Promise<string[]> {
  return insertWorksheetsFromBase64ViaIntents(base64File, options, timeoutMs);
}

/**
 * Insert a graph into the active worksheet from FDSChart XML via the
 * Spreadsheet intent bridge. Used by the chart-insertion path when an
 * `activeGraphContent` payload is available; the data-only fallback in
 * `excelService.ts` handles the absent case.
 *
 * **Pass the bare XML, not the wire-level envelope.** The wrapper
 * inside `addActiveGraphViaIntents` prepends the host's required
 * `COORD_TOP=N,COORD_LEFT=N,COORD_WIDTH=N,COORD_HEIGHT=N,CHART_XML=`
 * envelope automatically with sensible defaults (anchor near the top
 * of the active sheet, 320x200 8:5 landscape). Pass an
 * `AddActiveGraphCoords` object as the second arg to override anchor /
 * size. See `addActiveGraphViaIntents` JSDoc for the wire shape and
 * pre-wrapped-string sniff behaviour.
 *
 * **Return value is a host status message, NOT a sheet name.** The
 * intent reply's `message` field is a human-readable string like
 * `"Active graph added successfully"`. Do not feed it back to follow-up
 * intents that take a `sheetName` / `sheetNames` filter — passing a
 * status string into such a filter would silently match zero sheets.
 * If you need the actual landing sheet, follow up with
 * `getActiveExcelContext`.
 */
export async function addActiveGraph(
  fdsChartXml: string,
  coords?: AddActiveGraphCoords
): Promise<string> {
  return addActiveGraphViaIntents(fdsChartXml, coords);
}

export type { AddActiveGraphCoords };

/**
 * Replace the FDSChart XML of an existing ActiveGraph in-place via the
 * `factset.Charting.SetActiveGraphXml` intent.
 *
 * **NOT exposed to the planner.** The planner-emittable
 * `ActiveGraphModification.action` union no longer includes
 * `"update-selected"`, and the applier branch is a defensive
 * `trackSkip`. This dispatcher remains available for explicit
 * non-agent callers (dev-tools dialog, a future verifier hook) — the
 * underlying intent is too immature for the LLM to round-trip safely.
 *
 * Unlike {@link addActiveGraph} (which inserts a brand-new ActiveGraph OLE
 * object at given coords), this mutates an existing chart: same shape,
 * same position, new content. Pass `sheetRef + chartIndex` to target a
 * runtime-resolved chart; omit them to use the host's currently-selected
 * ActiveGraph fallback.
 *
 * **Encoding contract — bare XML, no envelope.** Unlike `addActiveGraph`,
 * this intent takes the raw `<FDSChart …>…</FDSChart>` body directly
 * (no `COORD_*,CHART_XML=` wrapping, no `&cmma` comma re-encoding); pass
 * the XML through as-is. See `setActiveGraphXmlViaIntents` for the full
 * wire-shape contract.
 *
 * **`refresh` semantics.** Defaults to `true` — the host re-runs the
 * chart's data binding so the rendered chart reflects the new XML
 * immediately. Pass `false` only for batched / styling-only mutations
 * where deferring the visual refresh is intentional.
 *
 * **Namespace note.** Like {@link getActiveGraphXml}, this lives in the
 * host-agnostic `factset.Charting.*` namespace (NOT
 * `factset.Spreadsheet.*`) and is reusable from PowerPoint.
 */
export async function setActiveGraphXml(
  fdsChartXml: string,
  options: SetActiveGraphXmlOptions = {}
): Promise<string> {
  return setActiveGraphXmlViaIntents(fdsChartXml, options);
}

/**
 * Render a FactSet chart preview via the `factset.PreviewChart` intent.
 *
 * Returns `{ fdsChartXml, pngDataUri, annotationsJson, ... }`. The XML
 * is fed to `addActiveGraph` on insert; the PNG is rendered inline in
 * the chat preview. See `previewChartViaIntents` for the full envelope-
 * normalisation contract — in particular, the host wraps the response
 * in an HTTP-style `{ data: { status, contentType, body: { data: ... } } }`
 * envelope inside `result.context`, which the wrapper unwraps.
 *
 * **Namespace note.** This intent is NOT in the `factset.Spreadsheet.*`
 * namespace, and uses an inconsistent casing convention across its two
 * envelope fields:
 *   - **Wire intent name:** `factset.PreviewChart` (CapitalizedCamelCase)
 *   - **Context `type`:**   `factset.previewChart` (lowerCamelCase)
 * Both forms are sent verbatim — see `PREVIEW_CHART_INTENT_NAME` and
 * `PREVIEW_CHART_CONTEXT_TYPE` in `spreadsheetIntents.ts`. If FactSet
 * later normalises either, flip the corresponding constant there
 * (single edit, no callers change).
 */
export async function previewChart(
  parameters: FactsetChartParameters,
  options?: PreviewChartOptions
): Promise<FactsetChartPreview> {
  return previewChartViaIntents(parameters, options);
}

/**
 * Request a downloadable Excel chart workbook via the
 * `factset.DownloadExcelChart` intent.
 *
 * Returns `.xlsx` bytes containing a **native Excel chart**
 * (`xl/charts/chart1.xml`) — portable, fully editable in any Excel
 * (Office, web, LibreOffice, Sheets via import), no FactSet
 * dependency. Use this when the user wants to share a chart with
 * someone outside FactSet.
 *
 * **Sibling**: `downloadActiveGraph` (below) returns the same
 * `.xlsx` MIME but with the chart embedded as a FactSet ActiveGraph
 * OLE object instead of a native Excel chart. See its dispatcher
 * doc for the product distinction.
 *
 * **Namespace + casing notes** — same conventions as `previewChart`:
 *   - **Wire intent name:** `factset.DownloadExcelChart`
 *     (CapitalizedCamelCase) — captured 2026-04-30
 *   - **Context `type`:**   `factset.downloadExcelChart`
 *     (lowerCamelCase) — captured 2026-04-30
 * Both forms are sent verbatim by the wrapper.
 *
 * **Workbook opening compatibility.** Some Excel host builds reject
 * `InsertWorksheetsFromBase64` with `addWorkbook.enabled = true` even though
 * the field is documented. The native-chart executor therefore uses the
 * supported `AddWorkbook` followed by a targeted worksheet insertion. Do not
 * collapse that sequence without validating the exact deployed host build.
 */
export async function downloadExcelChart(
  parameters: FactsetChartParameters,
  options?: DownloadExcelChartOptions
): Promise<FactsetDownloadExcelChart> {
  return downloadExcelChartViaIntents(parameters, options);
}

/**
 * Request a workbook with a FactSet ActiveGraph OLE-embedded
 * inside, via the `factset.DownloadActiveGraph` intent.
 *
 * Returns `.xlsx` bytes whose chart is encoded as a FactSet
 * ActiveGraph OLE object (`xl/embeddings/oleObject1.bin`) plus a
 * static EMF preview image (`xl/media/image1.emf`) used as the
 * OLE's visual fallback. The workbook is NOT portable in the way
 * `downloadExcelChart`'s output is — outside FactSet workstations
 * the recipient sees the EMF image only; inside FactSet (with the
 * ActiveGraph add-in available), the OLE deserialises into a live,
 * re-editable FactSet chart.
 *
 * **When to call this vs `downloadExcelChart`.** A product / UX
 * decision, not an interchangeability question:
 *   - `downloadExcelChart`     → "share with anyone, anywhere"
 *   - `downloadActiveGraph`    → "embed a live FactSet chart in my
 *                                 own Excel model, re-editable
 *                                 through FactSet"
 *
 * **Open empirical question on `InsertWorksheetsFromBase64` for
 * OLE embeds.** A prior round of testing concluded that primitive
 * copied worksheet cell data only and dropped both chart drawings
 * and OLE embeds during import. The conclusion may have aged out
 * given the bridge has continued to evolve since; the sibling
 * native-chart path now relies on targeted worksheet insertion for
 * native chart drawings. If a future UI surface needs to consume
 * `downloadActiveGraph` bytes in Excel, use the supported
 * `AddWorkbook` then targeted insertion sequence and verify whether
 * the OLE round-trips. Today the realistic consumers are file-save
 * flows outside the iframe sandbox, where OLE fidelity is guaranteed
 * end-to-end.
 *
 * **Namespace + casing notes** — same conventions as
 * `downloadExcelChart`:
 *   - **Wire intent name:** `factset.DownloadActiveGraph`
 *     (CapitalizedCamelCase) — captured 2026-04-30
 *   - **Context `type`:**   `factset.downloadActiveGraph`
 *     (lowerCamelCase) — captured 2026-04-30
 *
 * No Finster UI consumer hits this yet. See
 * `excel-docs/intent-details.md` → "Download Active Graph".
 */
export async function downloadActiveGraph(
  parameters: FactsetChartParameters,
  options?: DownloadActiveGraphOptions
): Promise<FactsetDownloadActiveGraph> {
  return downloadActiveGraphViaIntents(parameters, options);
}

/**
 * Read the currently-active FactSet ActiveGraph chart's serialised
 * `<FDSChart>` XML via the `factset.Charting.GetActiveGraphXml`
 * intent. Pass-through over `getActiveGraphXmlViaIntents`.
 *
 * **NOT exposed to the planner.** The workbook-context extractor no
 * longer invokes this dispatcher, so the FDSChart XML never reaches
 * the LLM's context payload. Kept wired for the dev-tools FactSet
 * Tools dialog and explicit non-agent callers only — round-trip
 * mutations from the LLM are deferred until the surface stabilises.
 *
 * Inverse of `addActiveGraph` — the returned `fdsChartXml` is the
 * exact string `addActiveGraph` accepts as `activeGraphContent`,
 * making round-trip backup / restore one call each direction.
 *
 * **Namespace + routing notes** — `factset.Charting.*` (note: NOT
 * `factset.Spreadsheet.Charting.*`, which is a different
 * intent family despite the similar name):
 *   - **Wire intent name:** `factset.Charting.GetActiveGraphXml`
 *     (CapitalizedCamelCase) — captured 2026-05-06
 *   - **Request `context.type`:** `fdc3.nothing` (no input today —
 *     operates on whatever ActiveGraph chart the host considers
 *     "active" at call time)
 *   - **Response context type:** `factset.charting.getActiveGraphXmlResult`
 *     with the flat `{ fdsChartXml }` envelope (NOT the nested
 *     `data.body.data` form `previewChart` / `getStylesets` use)
 *   - **AppId routing:** `OI Remote Iframe`
 *
 * **Caveat — captured XML is fully-hydrated.** The XML embeds every
 * data point the host has captured for the chart, not just its
 * recipe. Round-tripping through `addActiveGraph` preserves the
 * captured values verbatim — correct for "stash a backup", wrong
 * for "send this chart's recipe to a teammate" (the recipient gets
 * captured data, not a fresh refresh).
 *
 * Caller should `try/catch` and `console.warn` on failure. A
 * resolved-but-empty `{ fdsChartXml: undefined }` means "no active
 * chart" — treat as a no-op rather than an error.
 */
export async function getActiveGraphXml(
  options?: GetActiveGraphXmlOptions
): Promise<FactsetGetActiveGraphXmlResult> {
  return getActiveGraphXmlViaIntents(options);
}

/**
 * Read the host's styleset catalog via the `factset.GetStylesets`
 * intent.
 *
 * Returns `{ stylesets, currentStylesetId? }`. Each `stylesets[].id`
 * is exactly the value the chart-family intents (`previewChart`,
 * `downloadExcelChart`, `downloadActiveGraph`) accept on
 * `chartParameters.stylesetId`; `name` is the human-friendly label
 * suitable for a picker UI. `currentStylesetId` is the host's active
 * default (matches one of `stylesets[].id`), or `undefined` when the
 * host did not surface one.
 *
 * **Namespace + casing notes** — same conventions as `previewChart`
 * and the `download*` siblings:
 *   - **Wire intent name:** `factset.GetStylesets`
 *     (CapitalizedCamelCase) — captured 2026-05-01
 *   - **Request `context.type`:** `fdc3.nothing` (no input today)
 *   - **Response context type:** `factset.common.getStylesetsResult`
 *     with the two-level `data.body.data` envelope (NOT the flatter
 *     `download*` envelope)
 *
 * Caller should `try/catch` and `console.warn` on failure — this is
 * a best-effort enrichment read for chart UIs and must never block
 * a primary chart preview / insert path. An empty `stylesets: []`
 * is a valid "host didn't surface any" baseline; callers should
 * fall back to omitting `stylesetId` on the chart-family intents
 * (which the wrapper defaults to anyway).
 */
export async function getStylesets(
  options?: GetStylesetsOptions
): Promise<FactsetGetStylesetsResult> {
  return getStylesetsViaIntents(options);
}

/**
 * Read the host's template library catalog via the
 * `factset.GetTemplatesList` intent.
 *
 * Returns `{ templates }` — every template the host's library exposes,
 * in host order. Each entry carries:
 *   - `name` / `description` / `extendedDescription` for picker copy.
 *   - `category` for grouping (folder-style strings — preserve verbatim,
 *     including the fullwidth `＆` U+FF06 the host emits).
 *   - `key` — lowercased `.xlsx` filename, the host's stable per-template
 *     identifier; prefer over `name` for keys / dedup / cross-refresh
 *     diffs.
 *   - `templateUrl` / `previewUrl` — URLs to fetch the `.xlsx` bytes
 *     and the PDF preview (`previewUrl` is `null` when the host has no
 *     preview for the template).
 *   - `metadata.inputs[]` — input parameters the template wants at
 *     instantiation time (e.g. `"main_identifier"`, `"start_date"`,
 *     `"currency"`, `"frequency"`).
 *   - `metadata.identifierGroups` — open-ended map (`{ "group1":
 *     { "nbEntries": N }, ... }`) for templates that take
 *     multi-identifier slots.
 *
 * **Namespace + casing notes** — same conventions as `previewChart`,
 * `download*`, and `getStylesets`:
 *   - **Wire intent name:** `factset.GetTemplatesList`
 *     (CapitalizedCamelCase) — captured 2026-05-01
 *   - **Request `context.type`:** `fdc3.nothing` (no input today)
 *   - **Response context type:** `factset.common.getTemplatesListResult`
 *     with the two-level `data.body.data` envelope. Unlike
 *     `getStylesets`, the inner `data` is a **raw ARRAY** of
 *     templates — the wrapper presents it under the `templates` field
 *     for symmetry.
 *
 * Caller should `try/catch` and `console.warn` on failure — this is
 * a best-effort enrichment read for template pickers and must never
 * block a primary flow. An empty `templates: []` is a valid "host
 * didn't surface any" baseline; callers should render an empty-state
 * picker and / or fall back to the user's manual template upload path.
 */
export async function getTemplatesList(
  options?: GetTemplatesListOptions
): Promise<FactsetGetTemplatesListResult> {
  return getTemplatesListViaIntents(options);
}

/**
 * Read the entitlement-filtered template-key list via the
 * `factset.GetUserAvailableTemplates` intent.
 *
 * Returns `{ templateKeys }` — every `FactsetTemplate.key` the
 * current user is entitled to instantiate, in host order. **Pair
 * with `getTemplatesList`** to render an entitlement-aware picker:
 *
 * ```ts
 * const [catalog, entitled] = await Promise.all([
 *   getTemplatesList(),
 *   getUserAvailableTemplates(),
 * ]);
 * const allowed = new Set(entitled.templateKeys);
 * const visible = catalog.templates.filter(t => allowed.has(t.key));
 * ```
 *
 * **Namespace + casing notes** — same conventions as `previewChart`,
 * `download*`, `getStylesets`, and `getTemplatesList`:
 *   - **Wire intent name:** `factset.GetUserAvailableTemplates`
 *     (CapitalizedCamelCase) — captured 2026-05-01
 *   - **Request `context.type`:** `fdc3.nothing` (no input today)
 *   - **Response context type:**
 *     `factset.common.getUserAvailableTemplatesResult` with the
 *     two-level `data.body.data` envelope. Inner `data` is a flat
 *     `string[]` of lowercased `.xlsx` filenames — the wrapper
 *     surfaces it under the `templateKeys` field for forward-compat
 *     with a future host extension that returns per-entry
 *     entitlement metadata.
 *
 * Caller should `try/catch` and `console.warn` on failure — this is
 * a best-effort enrichment read for template pickers and must never
 * block a primary flow. An empty `templateKeys: []` is a valid "this
 * user can't instantiate any templates" baseline; callers should
 * surface an empty-state picker accordingly.
 */
export async function getUserAvailableTemplates(
  options?: GetUserAvailableTemplatesOptions
): Promise<FactsetGetUserAvailableTemplatesResult> {
  return getUserAvailableTemplatesViaIntents(options);
}

/**
 * Fetch the raw `.xlsx` bytes for a single template by `key`, via the
 * `factset.DownloadTemplate` intent.
 *
 * Pairs with `getTemplatesList` (catalog) and
 * `getUserAvailableTemplates` (entitlement filter): the caller picks
 * a `FactsetTemplate`, takes its `key`, and asks the host for the
 * raw template bytes — **without instantiating it**. Use
 * `refreshCalculate` instead when the user wants the template filled
 * in with input values.
 *
 * Two natural product surfaces today:
 *
 *   - "Save template to disk." Hand `result.workbookBase64` to a
 *     `Blob` / `<a download>` / `msSaveOrOpenBlob` flow.
 *   - "Open template inside Finster." Feed the bytes into a
 *     pipeline that opens the `.xlsx` (note: the host's
 *     `factset.Spreadsheet.InsertWorksheetsFromBase64` primitive
 *     copies worksheet cell data only — fine for a vanilla
 *     template, but a future template that ships native charts
 *     would lose them through that primitive).
 *
 * **Namespace + casing notes** — same conventions as `previewChart`,
 * `download*`, `getStylesets`, `getTemplatesList`,
 * `getUserAvailableTemplates`:
 *   - **Wire intent name:** `factset.DownloadTemplate`
 *     (CapitalizedCamelCase) — captured 2026-05-05
 *   - **Request `context.type`:** `factset.downloadTemplate`
 *     (lowerCamelCase). The `key` field sits as a TOP-LEVEL field
 *     on the context object alongside `type`, NOT inside a
 *     stringified `body` blob (compare with `refreshCalculate`).
 *   - **Response context type:**
 *     `factset.common.downloadTemplateResult` with the FLATTER
 *     one-level envelope: `data.bodyAsBase64` directly under `data`,
 *     same as `downloadExcelChart` / `downloadActiveGraph`. The
 *     wrapper surfaces the bytes as `workbookBase64` for naming
 *     symmetry with the chart-download wrappers.
 *
 * **Error semantics — same table as `downloadExcelChart` /
 * `downloadActiveGraph`:**
 *   - Non-200 `status` → `error = "Host returned status N"`.
 *   - 200 but no `bodyAsBase64` → `error = "Host returned status 200 but no bodyAsBase64"`.
 *   - 200 with bytes → `error = null` (success).
 *   - No `status` at all but bytes present → `error = undefined`
 *     (caller should treat as success — bytes are the source of
 *     truth).
 *
 * Throws on transport / timeout errors or invalid input (empty
 * `key`).
 */
export async function downloadTemplate(
  request: FactsetDownloadTemplateRequest,
  options?: DownloadTemplateOptions
): Promise<FactsetDownloadTemplate> {
  return downloadTemplateViaIntents(request, options);
}

/**
 * Kick off async template instantiation via the
 * `factset.RefreshCalculate` intent — the first WRITE intent in the
 * `OI Remote Iframe` group.
 *
 * Pairs with `getTemplatesList` (catalog) and
 * `getUserAvailableTemplates` (entitlement filter): the caller picks
 * a `FactsetTemplate`, collects values for each
 * `metadata.inputs[].inputType`, and invokes this dispatcher to ask
 * the host to compute the workbook.
 *
 * **Returns an async job handle, not the finished workbook.** The
 * captured response is HTTP 202 ("Accepted") with `status: "queued"`
 * — the actual computation runs server-side. The `id` (UUID) on the
 * returned `FactsetRefreshCalculateResult` is the handle the caller
 * must round-trip through the sibling refresh intents:
 * `getRefreshStatus` (poll status) and `getRefresh` (fetch bytes /
 * status payload).
 *
 * **Namespace + casing notes** — same conventions as `previewChart`,
 * `download*`, `getStylesets`, `getTemplatesList`, and
 * `getUserAvailableTemplates`:
 *   - **Wire intent name:** `factset.RefreshCalculate`
 *     (CapitalizedCamelCase) — captured 2026-05-01
 *   - **Request `context.type`:** `factset.refreshCalculate` (NOT
 *     `fdc3.nothing` — the only `OI Remote Iframe` intent so far
 *     that takes structured input)
 *   - **Request `body` is a stringified JSON** carrying
 *     `{ data: { key, inputs } }`, NOT a structured object. The
 *     wrapper handles the serialisation; callers pass a typed
 *     `FactsetRefreshCalculateRequest`.
 *   - **Response context type:**
 *     `factset.common.refreshCalculateResult` with the two-level
 *     `data.body.data` envelope. Inner `data` is `{ id, status }`.
 *
 * Throws on synchronous-leg transport / timeout errors, on input
 * validation failures (invalid `key` or non-string input values),
 * and on any 4xx/5xx `httpStatus` from the host. Async-leg failures
 * (the actual computation failing server-side) surface on
 * `getRefreshStatus` / `awaitRefreshCalculate`.
 */
export async function refreshCalculate(
  request: FactsetRefreshCalculateRequest,
  options?: RefreshCalculateOptions
): Promise<FactsetRefreshCalculateResult> {
  return refreshCalculateViaIntents(request, options);
}

/**
 * Poll a `refreshCalculate` job's status via `factset.GetRefreshStatus`.
 *
 * One-shot: returns whatever the host reports right now. Use
 * `awaitRefreshCalculate` below for the typical wait-for-terminal-status
 * loop.
 *
 * **Wire shape (request — confirmed):**
 *
 *   ```
 *   context: {
 *     type: "factset.getRefreshStatus",
 *     refreshId: "<id-from-refreshCalculate>"
 *   }
 *   ```
 *
 *   `refreshId` is the value returned on
 *   `FactsetRefreshCalculateResult.id`. The wrapper translates the
 *   parameter name on the wire so the two intents pair cleanly at
 *   call sites.
 *
 * **Namespace + casing notes** — same conventions as `refreshCalculate`:
 *   - **Wire intent name:** `factset.GetRefreshStatus`
 *     (CapitalizedCamelCase)
 *   - **Request `context.type`:** `factset.getRefreshStatus` (lowerCamelCase)
 *   - **`appId`:** `"OI Remote Iframe"`
 *
 * **Response shape (captured 2026-05-05).** The host returns
 * `data.status` + `data.body.data` where inner body is `{ id, status }`.
 * Observed progression for one job id:
 *   - non-terminal: `httpStatus: 202`, `body.status: "executing"`
 *   - terminal-success: `httpStatus: 201`, `body.status: "created"`
 *
 * Throws on synchronous-leg transport / timeout errors and on any
 * non-2xx top-level `httpStatus`. A `200` response carrying a
 * still-pending body (e.g. `{ status: "queued" }`) is NOT a wrapper
 * error — caller should keep polling.
 */
export async function getRefreshStatus(
  refreshId: string,
  options?: GetRefreshStatusOptions
): Promise<FactsetGetRefreshStatusResult> {
  return getRefreshStatusViaIntents(refreshId, options);
}

/**
 * Fetch refresh-result payload for a `refreshCalculate` job via
 * `factset.GetRefresh`.
 *
 * This intent can return either:
 *  - workbook bytes (`workbookBase64`) when the result is ready, or
 *  - a status payload (`refreshStatus`) while the job is still in-flight.
 *
 * Pass `refreshId` from `refreshCalculate` / `getRefreshStatus`.
 * `orUrl` is an optional host-specific override for builds that expose
 * an office-refresh URL directly.
 */
export async function getRefresh(
  request: FactsetGetRefreshRequest,
  options?: GetRefreshOptions
): Promise<FactsetGetRefreshResult> {
  return getRefreshViaIntents(request, options);
}

/**
 * Wait for a `refreshCalculate` job to reach a terminal status by
 * polling `factset.GetRefreshStatus` on a timer. Thin pass-through
 * over `pollRefreshCalculate` from `spreadsheetIntents.ts`.
 *
 * Pairs with `refreshCalculate`:
 *
 * ```
 * const job = await refreshCalculate({ key, inputs });
 * const final = await awaitRefreshCalculate(job.id, {
 *   onUpdate: (r) => console.log("[Excel] refresh status:", r.body.status),
 * });
 * ```
 *
 * Defaults: `intervalMs: 1500`, `timeoutMs: 60_000`. Override per call
 * site — long-running templates with many series may need a 2–5 min
 * deadline.
 *
 * Resolves with the last `getRefreshStatus` result on terminal status
 * (succeeded **or** failed — caller inspects `body.status` /
 * `body.error` to decide). Rejects with a
 * `RefreshCalculatePollTimeoutError` (a regular `Error` with
 * `name === "RefreshCalculatePollTimeoutError"` and a `lastResult`
 * property) when the overall deadline is exceeded.
 */
export async function awaitRefreshCalculate(
  refreshId: string,
  options?: PollRefreshCalculateOptions
): Promise<FactsetGetRefreshStatusResult> {
  return pollRefreshCalculate(refreshId, options);
}

// ============================================================================
// IMAGE DISPATCHER
// ============================================================================

/**
 * Drop a base64-encoded image onto the active sheet via the Spreadsheet
 * intent bridge. Required input is a full `data:image/...;base64,...`
 * URI plus an `anchorCell`; `name`, `width`, and `height` are optional.
 *
 * Used by `excelService.ts::applyLogoModificationViaIntents` to replace
 * the legacy "logo insertions not supported" warn-skip in bridge mode.
 *
 * Throws if the bridge call fails or if the input fails the wrapper's
 * own shape validation. Callers that want a local-fallback path should
 * gate this with `shouldUseExcelIntentBridge()` and catch errors.
 */
export async function addImageFromBase64(
  options: AddImageFromBase64Options
): Promise<string> {
  return addImageFromBase64ViaIntents(options);
}

// ============================================================================
// NATIVE EXCEL CHART DISPATCHER
// ============================================================================

/**
 * Insert a native Excel chart via the Spreadsheet intent bridge.
 * Pass-through over `addExcelChartViaIntents`.
 *
 * The dispatcher takes wire-shape options directly: `sourceRange`,
 * `chartType` pre-mapped to the Excel COM `xl*` enum, and a point-anchored
 * `position` rectangle (Excel's native chart unit, 1pt = 1/72 inch). The
 * planner-friendly cell-anchored translation (cell letter → column index
 * → points, planner type enum → wire enum) lives at the call site in
 * `excelService.ts::applyChartCreateViaBridge`, keeping this dispatcher
 * generic so other callers (e.g. test fixtures or future shape-ops) can
 * pass wire shapes verbatim.
 *
 * Returns the host's parsed chart info (`{ id, index, name, … }`) so the
 * caller can fold the host-assigned chart `name` into its `trackOperation`
 * log and any follow-up update / delete needs the right `chartIndex`.
 *
 * Distinct from `addActiveGraph`: that intent embeds a FactSet
 * ActiveGraph OLE shape (FDSChart-runtime-bound); `addExcelChart` lands a
 * native Excel chart bound to a `sourceRange` via Excel's own engine.
 * Pick `addExcelChart` for planner-emitted `ChartModification.action =
 * "create"` driven by raw cell data, and `addActiveGraph` only when an
 * `activeGraphContent` blob is in hand from `previewChart`.
 */
export async function addExcelChart(
  options: AddExcelChartOptions
): Promise<AddExcelChartResult> {
  return addExcelChartViaIntents(options);
}

/**
 * Update chart-level properties on an existing native Excel chart via
 * the Spreadsheet intent bridge. Pass-through over
 * `updateExcelChartViaIntents`.
 *
 * Returns the host's structured `{ success, appliedProperties,
 * warnings }` payload so callers can confirm which properties were
 * applied (the host silently no-ops unknown / unsupported fields and
 * surfaces the rest in `warnings`). Used by
 * `excelService.ts::applyChartUpdateViaBridge` to honour
 * `ChartModification.action = "update"`.
 *
 * Series writes are **not** supported here — for series changes pick
 * `addSeriesToChart`, `updateSeriesInChart`, or `removeSeriesFromChart`
 * (not yet wired). Shape ops (delete / move / resize charts) are
 * pre-announced by FactSet on a separate intent family — see
 * `missing-intents-v0.md` → `#open-shape-ops`.
 */
export async function updateExcelChart(
  options: UpdateExcelChartOptions
): Promise<UpdateExcelChartResult> {
  return updateExcelChartViaIntents(options);
}

/**
 * Append a new data series to an existing chart via the Spreadsheet
 * intent bridge. Pass-through over `addSeriesToChartViaIntents`.
 *
 * Returns the host-assigned `{ success, seriesIndex }` so callers can
 * thread `seriesIndex` into a follow-up `updateSeriesInChart` /
 * `removeSeriesFromChart` call. Used by
 * `excelService.ts::applyChartAddSeriesViaBridge` to honour
 * `ChartModification.action = "addSeries"` (combo charts and
 * post-create series additions).
 *
 * Targeting note (per `f2_docs/excel/chart-tools.md`): provide the
 * full `workbookName` + `sheetRef` + `chartIndex` triple, OR omit all
 * three to operate on the active chart. Passing `workbookName` alone
 * is rejected by the host as "specify all three or none".
 */
export async function addSeriesToChart(
  options: AddSeriesToChartOptions
): Promise<AddSeriesToChartResult> {
  return addSeriesToChartViaIntents(options);
}

/**
 * Update properties on an existing series via the Spreadsheet intent
 * bridge. Pass-through over `updateSeriesInChartViaIntents`.
 *
 * Returns the host's structured `{ success, appliedProperties }` so
 * callers can confirm which fields actually landed (the host silently
 * no-ops unknown / unsupported keys, the same way `UpdateExcelChart`
 * does — but without a `warnings[]` array on this intent).
 *
 * Used by `excelService.ts::applyChartUpdateSeriesViaBridge` to honour
 * `ChartModification.action = "updateSeries"`. `seriesIndex` is the
 * 0-based slot returned by `addSeriesToChart` or
 * `Charting.GetCharts`'s series rows; same triple-or-none targeting
 * rule as the other Charting series intents.
 */
export async function updateSeriesInChart(
  options: UpdateSeriesInChartOptions
): Promise<UpdateSeriesInChartResult> {
  return updateSeriesInChartViaIntents(options);
}

/**
 * Remove a series from an existing chart via the Spreadsheet intent
 * bridge. Pass-through over `removeSeriesFromChartViaIntents`.
 *
 * Used by `excelService.ts::applyChartRemoveSeriesViaBridge` to honour
 * `ChartModification.action = "removeSeries"`. Removing a series
 * shifts the 0-based indices of every series after the removed slot,
 * so callers issuing multiple removes back-to-back should either
 * re-fetch indices via `Charting.GetCharts` between calls or remove
 * from highest-index to lowest.
 */
export async function removeSeriesFromChart(
  options: RemoveSeriesFromChartOptions
): Promise<RemoveSeriesFromChartResult> {
  return removeSeriesFromChartViaIntents(options);
}

/**
 * Capture a native Excel chart as a base64-encoded PNG image via
 * `factset.Spreadsheet.Charting.GetChartImageAsBase64`.
 *
 * **Bridge-only.** Office.js has no native equivalent for "render
 * this chart object as a PNG" — the closest workaround would be a
 * full screenshot of the chart frame, which we don't ship. Callers
 * in non-bridge environments must gate with
 * `shouldUseExcelIntentBridge()` and handle the unsupported case
 * explicitly.
 *
 * Read-side / out-of-band tool — NOT planner-exposed. Consumers are
 * UI flows (chat-bubble chart preview, "save this chart as image"
 * affordance) and image-validation loops where a vision model
 * verifies the result of a chart-write intent.
 *
 * Returns the host's full data URI (`"data:image/png;base64,…"`) so
 * the result can be dropped into an `<img src="…">` tag verbatim
 * (per [`f2_docs/excel/chart-tools.md`](../../../../../../f2_docs/excel/chart-tools.md), updated
 * 2026-05-13 / f2_docs PR #56). Callers that need raw bytes should
 * strip everything up to and including the first `,`. For pre-2026-05-13
 * host builds that returned a bare base64 string under `imageBase64` /
 * `result`, the wrapper synthesises a `data:image/png;base64,…` URI
 * so the return contract is stable.
 *
 * **`format` is accepted but ignored** by the host atlas — output is
 * always PNG regardless of what `format` the caller passes. The
 * field is retained on the wire purely for backward compatibility
 * with the `oi-remote-iframe` Zod schema; new callers should omit
 * it. If supplied, accepted values are `"Png" | "Jpg" | "Bmp" |
 * "Gif"` (capitalized); the wrapper auto-translates the legacy
 * `"Jpeg"` spelling to `"Jpg"` with a one-line deprecation warning.
 *
 * Omit both `sheetRef` and `chartIndex` to target the currently
 * active chart.
 */
export async function getChartImageAsBase64(
  options: GetChartImageAsBase64Options
): Promise<string> {
  return getChartImageAsBase64ViaIntents(options);
}

export type { GetChartImageAsBase64Options, ChartImageFormat };

// ============================================================================
// SHAPE DISPATCHERS
// ============================================================================
//
// Close the long-standing chart-action gap from §4.3 of
// `missing-intents-v0.md`. Reachable via `ShapeModification.action`
// (delete | modify | select) and from direct chart/UI flows. Office.js
// fallbacks live in `excelService.ts::applyShapeModificationViaIntents`
// — the dispatcher itself is a thin pass-through.

/**
 * Delete a shape (chart, ActiveGraph OLE embed, image, etc.) by host-
 * assigned numeric id via the Spreadsheet intent bridge.
 *
 * **Active-sheet only today.** The captured wire payload has no
 * `sheetName` field; the wrapper accepts an optional `sheetName` and
 * sends it only when set, so today's host build is happy and a
 * future cross-sheet host extension is forward-compatible. See
 * `missing-intents-v0.md` §2.2 for the catalog clarification request.
 */
export async function deleteShape(shape: DeleteShapeRef): Promise<void> {
  return deleteShapeViaIntents(shape);
}

/**
 * Move and/or resize one or more shapes via the Spreadsheet intent
 * bridge. Each entry's `top` / `left` / `height` / `width` are in
 * **points** (Excel's native unit, matching `AddExcelChart.position`).
 * Cell-anchored planner inputs translate at the caller layer
 * (`excelService.ts`).
 */
export async function modifyShapes(
  shapeModifications: ShapeModificationEntry[]
): Promise<void> {
  return modifyShapesViaIntents(shapeModifications);
}

/**
 * Select one or more shapes by id via the Spreadsheet intent bridge.
 * Pure cursor / UX action — does NOT mutate the workbook, only
 * updates "which shapes are currently selected". Useful as a
 * follow-up after `modifyShapes` so the user lands on the shapes
 * the assistant just touched.
 *
 * Host returns `success: false, error: "Shape with ID N was not
 * found"` for missing ids; the wrapper propagates that error
 * verbatim through the thrown message.
 */
export async function selectShapes(options: SelectShapesOptions): Promise<void> {
  return selectShapesViaIntents(options);
}

export type { DeleteShapeRef, ShapeModificationEntry, SelectShapesOptions };

// ============================================================================
// PANE / FREEZE DISPATCHERS
// ============================================================================

/**
 * Freeze or unfreeze panes on a worksheet via the Spreadsheet intent
 * bridge. `row` / `column` indices in `options` are sent through
 * verbatim — they target the row/column AT which to freeze (host-
 * side convention).
 *
 * Reachable via `WorksheetModification.freezePanes` from the planner
 * (see `excelService.ts::applyWorkbookModificationsViaIntents`).
 * Returns the host's success message when present.
 */
export async function freezeUnfreezePanes(
  freezeMode: FreezeMode,
  options?: FreezeUnfreezePanesOptions
): Promise<string> {
  return freezeUnfreezePanesViaIntents(freezeMode, options);
}

/**
 * Read the current freeze-pane state via the OI Remote Iframe
 * `factset.GetPaneStatus` intent.
 *
 * **Bridge-only.** No Office.js fallback — the closest equivalent
 * would be `Worksheet.freezePanes` introspection but the round-trip
 * cost and divergent shape don't justify wiring it. Callers in non-
 * bridge environments must gate with `shouldUseExcelIntentBridge()`.
 *
 * Read-side / out-of-band tool — NOT planner-exposed. Consumers are
 * UI flows (e.g. `FactsetToolsDialog` dev panel, future "freeze
 * state badge" affordance).
 *
 * Response shape is INFERRED (see `getPaneStatusViaIntents` TODO
 * note); capture on first call and refine the wrapper.
 */
export async function getPaneStatus(
  options?: GetPaneStatusOptions
): Promise<PaneStatus> {
  return getPaneStatusViaIntents(options);
}

export type {
  FreezeMode,
  FreezeUnfreezePanesOptions,
  GetPaneStatusOptions,
  PaneStatus,
};

// ============================================================================
// TTF FORMULA DISPATCHER
// ============================================================================

/**
 * Insert a FactSet Text-to-Formula API response into the active
 * spreadsheet via the Spreadsheet intent bridge. Pass-through over
 * `insertTtfApiResponseViaIntents`.
 *
 * Two producer paths feed this wrapper:
 *  1. The per-cell TTF dispatcher in
 *     `excelService.ts::applyWorkbookModificationsViaIntents` —
 *     primary producer. Builds the `apiResponse` in-process via
 *     `services/factset/ttfCellDispatch.ts` (which calls
 *     `runTtfQuery` + `adaptTtfApiResponse`) and writes it to the
 *     selected anchor cell.
 *  2. The `WorkbookOperation.action = "insertTtfApiResponse"` slot —
 *     used when a producer already has a pre-built `apiResponse`
 *     envelope and wants to dispatch it as a workbook-level operation.
 *
 * Both routes terminate here.
 */
export async function insertTtfApiResponse(
  apiResponse: TtfApiResponse,
  options?: InsertTtfApiResponseOptions
): Promise<void> {
  return insertTtfApiResponseViaIntents(apiResponse, options);
}

// ============================================================================
// ROW / COLUMN VISIBILITY DISPATCHERS
// ============================================================================

/**
 * Hide one or more columns via the Spreadsheet intent bridge. The
 * `address.address` payload must be a column-shaped A1 string (e.g.
 * `"A:A"`, `"B:D"`, `"$A:$C"`); cell- or row-shaped addresses are
 * rejected by the host. Callers should validate at the use site (see
 * `excelService.ts::applyRangeModificationViaIntents`).
 */
export async function hideColumns(address: RangeAddress): Promise<void> {
  await hideColumnsViaIntents(address);
}

/**
 * Hide one or more rows via the Spreadsheet intent bridge. The
 * `address.address` payload must be a row-shaped A1 string (e.g.
 * `"5:5"`, `"$5:$10"`); cell- or column-shaped addresses are rejected
 * by the host.
 */
export async function hideRows(address: RangeAddress): Promise<void> {
  await hideRowsViaIntents(address);
}

/**
 * Show (unhide) one or more columns via the Spreadsheet intent bridge.
 * Shape contract mirrors `hideColumns`: `address.address` must be
 * column-shaped (`"A:A"`, `"B:D"`, `"$A:$C"`).
 */
export async function showColumns(address: RangeAddress): Promise<void> {
  await showColumnsViaIntents(address);
}

/**
 * Show (unhide) one or more rows via the Spreadsheet intent bridge.
 * Shape contract mirrors `hideRows`: `address.address` must be
 * row-shaped (`"5:5"`, `"5:10"`, `"$5:$10"`).
 */
export async function showRows(address: RangeAddress): Promise<void> {
  await showRowsViaIntents(address);
}

// ============================================================================
// OUTLINE GROUPING DISPATCHERS
// ============================================================================
//
// Excel outline grouping primitives — pair adjacent columns or rows
// under a collapsible group so the user can hide / unhide them from
// the gutter. Reachable via `RangeModification.action` =
// `"groupColumns"` / `"groupRows"` / `"ungroupColumns"` /
// `"ungroupRows"` once `excelService.ts::applyRangeModificationViaIntents`
// is updated.
//
// Address-shape contract mirrors `hide*` / `show*`: column ops take
// column-shaped addresses (`"A:C"`, `"$A:$C"`); row ops take row-shaped
// addresses (`"5:10"`, `"$5:$10"`). Cell-shaped (`"A1:C1"`) and the
// orthogonal shape are rejected by the host — callers validate at the
// use site via `isColumnShapedAddress` / `isRowShapedAddress` in
// `excelService.ts`.

/**
 * Group one or more adjacent columns via the Spreadsheet intent bridge.
 * Address must be column-shaped (`"A:C"`, `"$A:$C"`). The host outlines
 * the columns at the default outline level — nested outline-level
 * support is not yet wrapped (`#open-outline-levels` in
 * `missing-intents-v0.md` §6).
 */
export async function groupColumns(address: RangeAddress): Promise<void> {
  await groupColumnsViaIntents(address);
}

/**
 * Group one or more adjacent rows via the Spreadsheet intent bridge.
 * Address must be row-shaped (`"5:10"`, `"$5:$10"`). Same outline-
 * level caveat as `groupColumns`.
 */
export async function groupRows(address: RangeAddress): Promise<void> {
  await groupRowsViaIntents(address);
}

/**
 * Remove an outline grouping on one or more adjacent columns via the
 * Spreadsheet intent bridge. Address must be column-shaped (`"A:C"`,
 * `"$A:$C"`). Captured response shape is `{ success: true }`.
 */
export async function ungroupColumns(address: RangeAddress): Promise<void> {
  await ungroupColumnsViaIntents(address);
}

/**
 * Remove an outline grouping on one or more adjacent rows via the
 * Spreadsheet intent bridge. Address must be row-shaped (`"5:10"`,
 * `"$5:$10"`). Captured response shape is `{ success: true }`.
 */
export async function ungroupRows(address: RangeAddress): Promise<void> {
  await ungroupRowsViaIntents(address);
}

// ============================================================================
// SELECTION DISPATCHER
// ============================================================================

/**
 * Move the host's active selection to `range` via the Spreadsheet
 * intent bridge. Pass-through over `selectRangeViaIntents`.
 *
 * Cursor / UX action only — does NOT write data, only updates "what's
 * highlighted" in the spreadsheet UI. Sheet targeting via
 * `range.worksheetName` is supported, so cross-sheet selects work in
 * one round-trip; the host activates the target sheet implicitly.
 *
 * Note: this intent routes through the `OI Remote Iframe` relay (see
 * `spreadsheetIntents.ts::SELECT_RANGE_APP_ID`) — the wrapper handles
 * the appId override, so call sites here just hand over the address.
 *
 * Returns the host's confirmation `message` (captured: `"Range
 * selected successfully"`) for the caller to thread into
 * `trackOperation` if useful; resolves to `""` when the host omits it.
 */
export async function selectRange(range: RangeAddress): Promise<string> {
  return selectRangeViaIntents(range);
}

// ============================================================================
// WORKBOOK INTROSPECTION READ DISPATCHERS
// ============================================================================

/**
 * List all workbooks currently open in the host via the Spreadsheet
 * intent bridge. Useful as best-effort enrichment of `WorkbookContent`
 * so the AI planner knows which workbook(s) the user has open.
 *
 * Caller should `try/catch` and `console.warn` on failure — this is a
 * best-effort read and must never block the primary read pipeline.
 */
export async function getOpenWorkbooks(): Promise<OpenWorkbookInfo[]> {
  return getOpenWorkbooksViaIntents();
}

/**
 * Open a saved workbook through the Spreadsheet intent bridge.
 *
 * @param filePath - Non-empty absolute path readable by the host.
 * @param options - Optional alert and external-link update behavior.
 * @returns The host's open result.
 */
export async function openWorkbook(
  filePath: string,
  options?: OpenWorkbookOptions
): Promise<OpenWorkbookResult> {
  return openWorkbookViaIntents(filePath, options);
}

/**
 * Save the active workbook or an explicitly named workbook.
 *
 * @param targetWorkbookName - Exact host name, or `undefined` for active.
 * @returns The host's save result and authoritative path when available.
 */
export async function saveWorkbook(
  targetWorkbookName?: string
): Promise<SaveWorkbookResult> {
  return saveWorkbookViaIntents(targetWorkbookName);
}

/**
 * Save the active or named workbook to a new path.
 *
 * @param filename - Absolute destination path including filename.
 * @param targetWorkbookName - Exact source name, or `undefined` for active.
 * @returns The host's save result and authoritative path when available.
 */
export async function saveWorkbookAs(
  filename: string,
  targetWorkbookName?: string
): Promise<SaveWorkbookResult> {
  return saveWorkbookAsViaIntents(filename, targetWorkbookName);
}

/**
 * List external data connections in a workbook via the Spreadsheet
 * intent bridge.
 *
 * **`workbookName` is required (AI-2617).** `excel-tools.md` documents
 * it as `Required: yes` and the catalog's `fdc3.nothing` shortcut
 * does NOT cover this intent. Callers must thread the active
 * workbook name (from `safeGetActiveExcelContext()` /
 * `WorkbookContent.workbookName`); the wrapper pre-validates and
 * throws fail-fast when missing. Same warn-on-failure guidance as
 * `getOpenWorkbooks` — an empty `connections: []` is a valid "no
 * data" baseline (matches the captured example in
 * `excel-docs/intent-details.md`).
 */
export async function getWorkbookConnections(
  workbookName: string
): Promise<WorkbookConnection[]> {
  return getWorkbookConnectionsViaIntents(workbookName);
}

/**
 * List named ranges and named formulas in a workbook via the
 * Spreadsheet intent bridge. Pass `skipHidden: true` to suppress
 * internal/hidden named objects that FactSet's infrastructure pins.
 *
 * **`workbookName` is required (AI-2617).** Callers must thread the
 * active workbook name (from `safeGetActiveExcelContext()` /
 * `WorkbookContent.workbookName`) — the wrapper pre-validates and
 * throws fail-fast when missing. Same warn-on-failure guidance as the
 * other workbook-introspection dispatchers — callers wrap in
 * try/catch so a failure (or a missing workbook name) never blocks
 * the primary read pipeline.
 */
export async function getWorkbookNamedObjects(
  options: GetWorkbookNamedObjectsOptions
): Promise<WorkbookNamedObject[]> {
  return getWorkbookNamedObjectsViaIntents(options);
}

/**
 * Create a workbook named object (named range / named formula) via the
 * Spreadsheet intent bridge.
 */
export async function addNamedObject(
  options: AddNamedObjectOptions
): Promise<void> {
  await addNamedObjectViaIntents(options);
}

/**
 * Remove a workbook named object (named range / named formula) via the
 * Spreadsheet intent bridge.
 */
export async function removeNamedObject(
  options: RemoveNamedObjectOptions
): Promise<void> {
  await removeNamedObjectViaIntents(options);
}

/**
 * Search workbook content via the Spreadsheet intent bridge.
 *
 * Returns best-effort normalized matches plus the raw host envelope.
 * This is a read-only operation (no workbook mutation).
 */
export async function searchWorkbook(
  options: SpreadsheetSearchOptions
): Promise<SpreadsheetSearchResult> {
  return searchViaIntents(options);
}

// ============================================================================
// NOTE / THREADED-COMMENT DISPATCHERS  (bridge-only)
// ============================================================================

/** Get threaded (purple) comments from one cell. */
export async function getComment(
  referenceAddress: RangeAddress,
  signal?: AbortSignal,
): Promise<SpreadsheetCellComment[]> {
  return getCommentViaIntents(referenceAddress, undefined, signal);
}

/** Add or replace a threaded (purple) comment on one cell. */
export async function setComment(options: SetCommentOptions): Promise<void> {
  await setCommentViaIntents(options);
}

/** Delete threaded comments from one or more cells. */
export async function deleteComment(
  referenceAddresses: RangeAddress[]
): Promise<void> {
  await deleteCommentViaIntents(referenceAddresses);
}

/** List threaded comments in the requested workbook/sheets. */
export async function getAllComments(
  options?: GetAllAnnotationsOptions,
  signal?: AbortSignal,
): Promise<SpreadsheetCommentEntry[]> {
  return getAllCommentsViaIntents(options, undefined, signal);
}

/** Get the yellow sticky-note text from one cell. */
export async function getNote(
  referenceAddress: RangeAddress,
  signal?: AbortSignal,
): Promise<string> {
  return getNoteViaIntents(referenceAddress, undefined, signal);
}

/** Add or replace a yellow sticky note on one cell. */
export async function setNote(options: SetNoteOptions): Promise<void> {
  await setNoteViaIntents(options);
}

/** Delete notes from one or more cells. */
export async function deleteNote(
  referenceAddresses: RangeAddress[]
): Promise<void> {
  await deleteNoteViaIntents(referenceAddresses);
}

/** List yellow sticky notes in the requested workbook/sheets. */
export async function getAllNotes(
  options?: GetAllAnnotationsOptions,
  signal?: AbortSignal,
): Promise<SpreadsheetNoteEntry[]> {
  return getAllNotesViaIntents(options, undefined, signal);
}

export type {
  SetCommentOptions,
  SetNoteOptions,
  GetAllAnnotationsOptions,
  SpreadsheetCellComment,
  SpreadsheetCommentEntry,
  SpreadsheetNoteEntry,
};

// ============================================================================
// SNAPSHOT / EXPORT DISPATCHERS  (bridge-only read-side primitives)
// ============================================================================
//
// Three out-of-band primitives that surface workbook content as bytes
// (PNG range snapshot, base64 .xlsx export). Read-side / UI-facing —
// NOT planner-exposed. The planner stays on `getRangeInfo` for range
// reads; these dispatchers feed UI affordances (chat-bubble preview
// images, "download these sheets" buttons, dev panels).
//
// **All bridge-only.** No Office.js fallback exists for "render this
// range as a PNG" or "serialise these sheets to a base64 .xlsx" —
// the closest Office.js workaround would be a manual screenshot
// (snapshot) or a `Workbook.save()` + file-system round-trip
// (worksheets export), neither of which we ship. Callers in non-
// bridge environments must gate with `shouldUseExcelIntentBridge()`.

/**
 * Capture a range as a base64 PNG via the Spreadsheet intent bridge.
 *
 * **Bridge-only — see section header.** Read-side / out-of-band tool
 * for UI flows (chat-bubble range previews, the "save this range as
 * image" affordance) — NOT planner-exposed.
 *
 * Returns the raw base64 string (no `data:image/png;base64,` prefix);
 * the consumer prepends the data-URI prefix when wrapping for an
 * `<img>` `src`.
 */
export async function getRangeSnapshot(address: RangeAddress): Promise<string> {
  return getRangeSnapshotViaIntents(address);
}

/**
 * Export one or more worksheets as a base64 `.xlsx` via the
 * Spreadsheet intent bridge.
 *
 * **Bridge-only — see section header.** Read-side / out-of-band tool
 * for UI flows ("download these sheets as xlsx" affordance,
 * `FactsetToolsDialog` dev panel) — NOT planner-exposed.
 *
 * **Cataloged 2026-05-12** in `f2_docs/excel/excel-tools.md`
 * (⚗️ Experimental). The wrapper was wired pre-doc off a captured
 * payload and matches the published catalog row verbatim.
 *
 * **PascalCase → wire translation.** The catalog formalises (rather
 * than fixes) the PascalCase divergence: the host's `options.*` keys
 * are `WorkbookName` / `SheetNames` while the rest of the catalog
 * uses lowerCamelCase. The wrapper translates from Finster's
 * lowerCamelCase `{ workbookName?, sheetNames? }` to the host's
 * shape internally so callers stay consistent with the rest of the
 * API. Tracked in `missing-intents-v0.md` §2.2 as a remaining
 * FactSet ask to align.
 *
 * Returns the raw base64 string. Acts as an interim "export workbook
 * bytes" surface until `factset.Spreadsheet.ExportWorkbook` lands
 * (`missing-intents-v0.md` §6.4).
 */
export async function getWorksheetsAsBase64(
  options?: GetWorksheetsAsBase64Options
): Promise<string> {
  return getWorksheetsAsBase64ViaIntents(options);
}

export type { GetWorksheetsAsBase64Options };

// ============================================================================
// UNDO BRACKET DISPATCHERS
// ============================================================================

/**
 * Open an undo transaction via the Spreadsheet intent bridge so the
 * subsequent batch of mutations collapses into one user-facing undo
 * entry. Pair every successful `beginUndo` with an `endUndo` call —
 * `applyWorkbookModificationsViaIntents` mirrors the `setCalculationMode`
 * pattern with a parallel `undoBegun` flag and an `endUndo` in the same
 * `finally` block.
 *
 * Best-effort by contract: callers MUST wrap the call in their own
 * try/catch and `console.warn` on failure — undo bracket failure is
 * never a user-facing error and never blocks the apply.
 */
export async function beginUndo(options?: BeginUndoOptions): Promise<void> {
  await beginUndoViaIntents(options);
}

/**
 * Close the undo transaction opened by `beginUndo`. Same best-effort
 * contract — callers MUST wrap in their own try/catch and continue on
 * failure. Sends `fdc3.nothing` (the catalog defines `EndUndo` with no
 * input fields).
 */
export async function endUndo(): Promise<void> {
  await endUndoViaIntents();
}
